export const STANDARD_TOURNAMENT_CATEGORIES = [
  { id: 'mens_singles', label: "Men's Singles", filterLabel: "MEN'S" },
  { id: 'womens_singles', label: "Women's Singles", filterLabel: "WOMEN'S" },
  { id: 'mens_doubles', label: "Men's Doubles", filterLabel: "MEN'S DOUBLES" },
  { id: 'womens_doubles', label: "Women's Doubles", filterLabel: "WOMEN'S DOUBLES" },
  { id: 'mixed_doubles', label: 'Mixed Doubles', filterLabel: 'MIXED DOUBLES' },
];

/** Legacy Men's/Women's Mixed Doubles IDs → unified Mixed Doubles. */
export const LEGACY_CATEGORY_ALIASES = {
  mens_mixed_doubles: 'mixed_doubles',
  womens_mixed_doubles: 'mixed_doubles',
};

export const normalizeCategoryId = (categoryId) => {
  const id = String(categoryId || '').trim();
  return LEGACY_CATEGORY_ALIASES[id] || id;
};

export const OPEN_SINGLES_CATEGORIES = [
  { id: 'open_mens_singles', label: "Open Men's Singles", filterLabel: "OPEN MEN'S" },
  { id: 'open_womens_singles', label: "Open Women's Singles", filterLabel: "OPEN WOMEN'S" },
];

/** Open-division pair events (Open Single skill). */
export const OPEN_DOUBLES_CATEGORIES = [
  { id: 'open_mens_doubles', label: "Open Men's Doubles", filterLabel: "OPEN MEN'S DOUBLES" },
  { id: 'open_womens_doubles', label: "Open Women's Doubles", filterLabel: "OPEN WOMEN'S DOUBLES" },
  { id: 'open_mixed_doubles', label: 'Open Mixed Doubles', filterLabel: 'OPEN MIXED DOUBLES' },
];

/** All Open Single formats: singles + doubles + mixed. */
export const OPEN_DIVISION_CATEGORIES = [
  ...OPEN_SINGLES_CATEGORIES,
  ...OPEN_DOUBLES_CATEGORIES,
];

export const TOURNAMENT_CATEGORIES = [
  ...STANDARD_TOURNAMENT_CATEGORIES,
  ...OPEN_DIVISION_CATEGORIES,
];

/** Tournament type filter pills — one per category for easy finding. */
export const CATEGORY_FILTER_PILLS = [
  { id: 'mens_singles', label: "MEN'S SINGLES" },
  { id: 'womens_singles', label: "WOMEN'S SINGLES" },
  { id: 'mens_doubles', label: "MEN'S DOUBLES" },
  { id: 'womens_doubles', label: "WOMEN'S DOUBLES" },
  { id: 'mixed_doubles', label: 'MIXED DOUBLES' },
  { id: 'open_mens_singles', label: "OPEN MEN'S SINGLES" },
  { id: 'open_womens_singles', label: "OPEN WOMEN'S SINGLES" },
  { id: 'open_mens_doubles', label: "OPEN MEN'S DOUBLES" },
  { id: 'open_womens_doubles', label: "OPEN WOMEN'S DOUBLES" },
  { id: 'open_mixed_doubles', label: 'OPEN MIXED DOUBLES' },
];

export const NEWBIE_SKILL = 'Newbie';
export const SKILL_LEVELS = [NEWBIE_SKILL, 'Novice', 'Intermediate', 'Advanced', 'Open Single'];

/** Organizer-only tournament skills. Junior = Under 18; Senior = 50+. Not player DUPR bands. */
export const JUNIOR_SKILL = 'Junior';
export const SENIOR_SKILL = 'Senior';
export const TOURNAMENT_SKILL_LEVELS = [...SKILL_LEVELS, JUNIOR_SKILL, SENIOR_SKILL];

/** Levels a player may choose on signup / skill review. Flex (Open Single) is admin-assigned only. */
export const CLIENT_SKILL_LEVELS = [NEWBIE_SKILL, 'Novice', 'Intermediate', 'Advanced'];

/**
 * Player-facing name for the stored "Open Single" skill.
 * Events stay "Open Single"; people with that skill are Flex — they can enter any division.
 */
export const FLEX_PLAYER_LABEL = 'Flex';
export const FLEX_PLAYER_HINT = 'Flexible — welcome in every division without changing your skill';

/** Normalize legacy "Beginner" → "Novice" and "Flex" → stored Open Single. */
export const normalizeSkillLevel = (value, fallback = 'Novice') => {
  const raw = String(value || '').trim();
  if (!raw) return fallback;
  const lower = raw.toLowerCase();
  if (lower === 'beginner') return 'Novice';
  if (lower === 'newbie' || lower === 'newb' || lower === 'new player') return NEWBIE_SKILL;
  if (lower === 'flex' || lower === 'flexible' || lower === 'flex player') return 'Open Single';
  if (lower === 'junior') return JUNIOR_SKILL;
  if (lower === 'senior' || lower === 'seniors') return SENIOR_SKILL;
  const match = TOURNAMENT_SKILL_LEVELS.find((level) => level.toLowerCase() === lower);
  return match || raw;
};

export const isNewbieSkill = (skill) => normalizeSkillLevel(skill, '') === NEWBIE_SKILL;
export const isJuniorSkill = (skill) => normalizeSkillLevel(skill, '') === JUNIOR_SKILL;
export const isSeniorSkill = (skill) => normalizeSkillLevel(skill, '') === SENIOR_SKILL;

export const isOpenSinglesCategory = (categoryId) =>
  String(categoryId || '').startsWith('open_');

export const isOpenSingleSkill = (skill) =>
  normalizeSkillLevel(skill, '') === 'Open Single';

/** Novice / Intermediate / Advanced require a DUPR band. Newbie, Junior, Senior, and custom catalog skills do not. */
export const DUPR_RATED_SKILLS = ['Novice', 'Intermediate', 'Advanced'];
export const skillSkipsDupr = (skill) => {
  const n = normalizeSkillLevel(skill, '');
  if (!n || isOpenSingleSkill(n)) return false;
  return !DUPR_RATED_SKILLS.includes(n);
};

/** inherit = follow site switch; required = force on; exempt = Flex path even when site is on. */
export const DUPR_MODE_INHERIT = 'inherit';
export const DUPR_MODE_REQUIRED = 'required';
export const DUPR_MODE_EXEMPT = 'exempt';

export const normalizeDuprMode = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'required' || raw === 'require' || raw === 'force' || raw === 'on') return DUPR_MODE_REQUIRED;
  if (raw === 'exempt' || raw === 'optional' || raw === 'off' || raw === 'skip') return DUPR_MODE_EXEMPT;
  return DUPR_MODE_INHERIT;
};

/**
 * Effective DUPR requirement for a player.
 * Site off wins for every player, including a per-user "required" override.
 * When the site switch is on: required forces DUPR, exempt uses the Flex path,
 * inherit follows the site switch (default on).
 */
export const effectiveDuprRequired = (user, globalRequired) => {
  if (globalRequired === false) return false;
  const mode = normalizeDuprMode(user?.dupr_mode);
  if (mode === DUPR_MODE_REQUIRED) return true;
  if (mode === DUPR_MODE_EXEMPT) return false;
  // Hydrated API payloads already include the effective flag — prefer that so a
  // still-loading site switch (context defaults to on) cannot re-gate the player.
  if (typeof user?.dupr_required === 'boolean') return !!user.dupr_required;
  if (typeof globalRequired === 'boolean') return globalRequired;
  if (typeof user?.dupr_required_global === 'boolean') return !!user.dupr_required_global;
  return true;
};

/** Saved tournament win on the account, or null when details or proof are missing. */
export const savedTournamentWin = (user) => {
  if (!user?.previous_winner) return null;
  const details = String(user.previous_winner_details || '').trim();
  const proofUrl = String(user.previous_winner_proof_url || '').trim();
  const proofFileId = String(user.previous_winner_proof_file_id || '').trim();
  const proofFilename = String(user.previous_winner_proof_filename || '').trim();
  if (!details || (!proofUrl && !proofFileId)) return null;
  return { details, proofUrl, proofFileId, proofFilename };
};

/** True when this person is a Flex player (stored skill is still "Open Single"). */
export const isFlexPlayer = (skill) => isOpenSingleSkill(skill);

/** Player badge / profile label — Flex instead of the event name Open Single. */
export const playerSkillLabel = (skill) =>
  (isFlexPlayer(skill) ? FLEX_PLAYER_LABEL : normalizeSkillLevel(skill, '')) || '';

/**
 * Open Single is the club's all-comers division: any skill level and any age may
 * enter, and nobody has to request a skill change first.
 */
export const isOpenSingleTournament = (tournament) =>
  isOpenSingleSkill(tournament?.skill_level) || isOpenSinglesCategory(tournament?.category);

export const OPEN_SINGLE_OPEN_TO_ALL_HINT = 'Open to all skill levels and ages — no skill change needed';

export const CATEGORY_FORMAT_SINGLES = 'singles';
export const CATEGORY_FORMAT_DOUBLES = 'doubles';
export const CATEGORY_FORMAT_MIXED = 'mixed';
export const CUSTOM_CATEGORY_FORMATS = [
  CATEGORY_FORMAT_SINGLES,
  CATEGORY_FORMAT_DOUBLES,
  CATEGORY_FORMAT_MIXED,
];

let runtimeExtraCategories = [];

export const slugifyCategoryName = (name) => String(name || '')
  .trim()
  .toLowerCase()
  .replace(/['’]/g, '')
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 40);

export const extraCategoryId = (name, format = CATEGORY_FORMAT_SINGLES) => {
  const slug = slugifyCategoryName(name);
  if (!slug) return '';
  const fmt = CUSTOM_CATEGORY_FORMATS.includes(format) ? format : CATEGORY_FORMAT_SINGLES;
  if (fmt === CATEGORY_FORMAT_MIXED) return `custom_mixed_doubles_${slug}`;
  if (fmt === CATEGORY_FORMAT_DOUBLES) return `custom_doubles_${slug}`;
  return `custom_singles_${slug}`;
};

export const parseExtraCategories = (raw) => {
  const extras = [];
  const seen = new Set();
  const lockedIds = new Set(TOURNAMENT_CATEGORIES.map((c) => c.id));
  const lockedLabels = new Set(TOURNAMENT_CATEGORIES.map((c) => String(c.label || '').toLowerCase()));
  (Array.isArray(raw) ? raw : []).forEach((item) => {
    const label = String(
      (typeof item === 'string' ? item : (item?.label || item?.name || '')),
    ).trim();
    if (!label || lockedLabels.has(label.toLowerCase())) return;
    const format = CUSTOM_CATEGORY_FORMATS.includes(item?.format)
      ? item.format
      : CATEGORY_FORMAT_SINGLES;
    const id = String(item?.id || extraCategoryId(label, format)).trim();
    if (!id || lockedIds.has(id) || seen.has(id)) return;
    seen.add(id);
    extras.push({
      id,
      label,
      filterLabel: String(item?.filterLabel || label).toUpperCase(),
      format,
    });
  });
  return extras;
};

export const setRuntimeExtraCategories = (extras) => {
  runtimeExtraCategories = parseExtraCategories(extras);
};

export const getRuntimeExtraCategories = () => runtimeExtraCategories;

export const allTournamentCategories = (extras) => {
  const extra = parseExtraCategories(extras ?? runtimeExtraCategories);
  const seen = new Set(TOURNAMENT_CATEGORIES.map((c) => c.id));
  return [...TOURNAMENT_CATEGORIES, ...extra.filter((c) => {
    if (seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  })];
};

const withExtraCategories = (base, extras) => {
  const extra = parseExtraCategories(extras ?? runtimeExtraCategories);
  const seen = new Set(base.map((c) => c.id));
  return [...base, ...extra.filter((c) => {
    if (seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  })];
};

/** Categories allowed for a skill: Open Single → open formats; else standard 5. Catalog extras append to every skill. */
export const categoriesForSkill = (skill, extras) => withExtraCategories(
  isOpenSingleSkill(skill) ? OPEN_DIVISION_CATEGORIES : STANDARD_TOURNAMENT_CATEGORIES,
  extras,
);

export const categoryAllowedForSkill = (categoryId, skill, extras) => {
  const normalized = normalizeCategoryId(categoryId);
  return categoriesForSkill(skill, extras).some((c) => c.id === normalized);
};

/**
 * Category chips/filters for browse UIs.
 * skillFilter === 'all' → every category; Open Single → open formats + extras.
 * Novice / Intermediate / Advanced keep the open formats too, because
 * Open Single events accept every skill level.
 */
export const categoriesForSkillFilter = (skillFilter, extras) => {
  if (!skillFilter || skillFilter === 'all') return allTournamentCategories(extras);
  if (isOpenSingleSkill(skillFilter)) return withExtraCategories(OPEN_DIVISION_CATEGORIES, extras);
  return withExtraCategories(
    [...STANDARD_TOURNAMENT_CATEGORIES, ...OPEN_DIVISION_CATEGORIES],
    extras,
  );
};

export const categoryFilterPillsForSkill = (skillFilter, extras) => {
  const allowed = categoriesForSkillFilter(skillFilter, extras);
  const allowedIds = new Set(allowed.map((c) => c.id));
  const pills = CATEGORY_FILTER_PILLS.filter((pill) => allowedIds.has(pill.id));
  const extraPills = allowed
    .filter((c) => !CATEGORY_FILTER_PILLS.some((pill) => pill.id === c.id))
    .map((c) => ({ id: c.id, label: (c.filterLabel || c.label || c.id).toUpperCase() }));
  return [...pills, ...extraPills];
};

export const SKILL_RANK = { Newbie: 0, Novice: 1, Intermediate: 2, Advanced: 3, 'Open Single': 4 };

export const skillRank = (value) => {
  const n = normalizeSkillLevel(value);
  if (Object.prototype.hasOwnProperty.call(SKILL_RANK, n)) return SKILL_RANK[n];
  if (!n) return 0;
  // Catalog extras are parallel divisions, not "below Novice".
  return 5;
};

export const listIncludesSkill = (list, skill) => {
  const n = normalizeSkillLevel(skill, '').toLowerCase();
  if (!n) return false;
  return (list || []).some((item) => String(item || '').trim().toLowerCase() === n);
};

export const skillSortIndex = (skill, orderedLevels = SKILL_LEVELS) => {
  const label = normalizeSkillLevel(skill, '');
  const idx = (orderedLevels || []).findIndex(
    (s) => String(s || '').toLowerCase() === String(label).toLowerCase(),
  );
  return idx >= 0 ? idx : 100 + (label ? label.charCodeAt(0) : 0);
};

/**
 * Playing-up is a DUPR-ladder idea (Novice into Intermediate, etc.).
 * Newbie, Junior/Senior, and custom catalog skills skip that comparison.
 */
export const isPlayingUpSkill = (playerSkill, divisionSkill, { playedUp = false } = {}) => {
  if (playedUp) return true;
  const division = normalizeSkillLevel(divisionSkill, '');
  const player = normalizeSkillLevel(playerSkill, '');
  if (!player || !division) return false;
  if (isOpenSingleSkill(division) || isOpenSingleSkill(player)) return false;
  if (skillSkipsDupr(division) || skillSkipsDupr(player)) return false;
  return skillRank(player) < skillRank(division);
};

/**
 * Low / High sub-tiers refine a base skill instead of replacing it, so a player
 * rated "Novice" still matches both Low Novice and High Novice events.
 * '' = the whole division, which is how every pre-existing event reads.
 */
export const SKILL_TIER_NONE = '';
export const SKILL_TIER_LOW = 'low';
export const SKILL_TIER_HIGH = 'high';
export const SKILL_TIERS = [SKILL_TIER_NONE, SKILL_TIER_LOW, SKILL_TIER_HIGH];
export const SKILL_TIER_LABELS = { [SKILL_TIER_LOW]: 'Low', [SKILL_TIER_HIGH]: 'High' };

export const normalizeSkillTier = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  return raw === SKILL_TIER_LOW || raw === SKILL_TIER_HIGH ? raw : SKILL_TIER_NONE;
};

export const skillTierLabel = (value) => SKILL_TIER_LABELS[normalizeSkillTier(value)] || '';

/** Open Single is a single pool — it takes no Low/High tier. */
export const tierAllowedForSkill = (skill, tier) => {
  if (!normalizeSkillTier(tier)) return true;
  if (isJuniorSkill(skill) || isSeniorSkill(skill)) return false;
  return !isOpenSingleSkill(skill);
};

/** Age division is not a skill: Open runs three trees; Kids / Seniors run one tree. */
export const AGE_DIVISION_OPEN = 'open';
export const AGE_DIVISION_KIDS = 'kids';
export const AGE_DIVISION_SENIORS = 'seniors';
export const AGE_DIVISIONS = [AGE_DIVISION_OPEN, AGE_DIVISION_KIDS, AGE_DIVISION_SENIORS];
export const KIDS_MAX_AGE = 17;
export const KIDS_DIVISION_LABEL = 'Kids / Junior';
export const JUNIOR_CATEGORY_PREFIX = 'Junior';
export const SENIOR_CATEGORY_PREFIX = 'Senior';
/** Public slot / bracket name — same idea as Under 50, ages 17 and under. */
export const UNDER_18_DIVISION_LABEL = 'Under 18';
export const AGE_GROUP_UNDER_18 = '0-17';
export const AGE_GROUP_50_PLUS = '50+';
export const BRACKET_DIVISION_KIDS = AGE_DIVISION_KIDS;

const AGE_CATEGORY_PREFIX_RE = /^(senior|junior|kids(?:\s*\/\s*junior)?)\s+/i;

export const normalizeAgeDivision = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === AGE_DIVISION_KIDS) return AGE_DIVISION_KIDS;
  if (raw === AGE_DIVISION_SENIORS || raw === 'senior') return AGE_DIVISION_SENIORS;
  return AGE_DIVISION_OPEN;
};

export const isKidsTournament = (tournament) =>
  normalizeAgeDivision(tournament?.age_division) === AGE_DIVISION_KIDS
  || isJuniorSkill(tournament?.skill_level)
  || String(tournament?.age_group || '').trim() === AGE_GROUP_UNDER_18;

export const isSeniorsTournament = (tournament) =>
  normalizeAgeDivision(tournament?.age_division) === AGE_DIVISION_SENIORS
  || isSeniorSkill(tournament?.skill_level)
  || String(tournament?.age_group || '').trim() === AGE_GROUP_50_PLUS;

/** Skill Junior/Senior and age brackets Under 18 / 50+ lock Age Division + age group. */
export const applyTournamentDivisionLocks = ({
  skill_level,
  age_division,
  age_group,
  skill_tier,
} = {}) => {
  const skill = skill_level;
  let division = normalizeAgeDivision(age_division);
  let group = String(age_group || '').trim();
  let tier = normalizeSkillTier(skill_tier);
  if (isJuniorSkill(skill)) {
    return {
      skill_level: JUNIOR_SKILL,
      age_division: AGE_DIVISION_KIDS,
      age_group: AGE_GROUP_UNDER_18,
      skill_tier: SKILL_TIER_NONE,
    };
  }
  if (isSeniorSkill(skill)) {
    return {
      skill_level: SENIOR_SKILL,
      age_division: AGE_DIVISION_SENIORS,
      age_group: AGE_GROUP_50_PLUS,
      skill_tier: SKILL_TIER_NONE,
    };
  }
  if (group === AGE_GROUP_UNDER_18) {
    division = AGE_DIVISION_KIDS;
    tier = SKILL_TIER_NONE;
  } else if (group === AGE_GROUP_50_PLUS) {
    division = AGE_DIVISION_SENIORS;
    tier = SKILL_TIER_NONE;
  } else if (division === AGE_DIVISION_KIDS) {
    group = AGE_GROUP_UNDER_18;
    tier = SKILL_TIER_NONE;
  } else if (division === AGE_DIVISION_SENIORS) {
    group = AGE_GROUP_50_PLUS;
    tier = SKILL_TIER_NONE;
  }
  if (isOpenSingleSkill(skill)) tier = SKILL_TIER_NONE;
  return {
    skill_level: skill,
    age_division: division,
    age_group: group,
    skill_tier: tier,
  };
};

/**
 * Parent / guardian government IDs used for official PH registration of a minor.
 * Kids/Junior players usually do not have their own ID — the photo must be the parent's.
 */
export const JUNIOR_PARENT_ID_TYPES = [
  { id: 'philsys', label: 'PhilSys National ID / ePhilID' },
  { id: 'passport', label: 'Philippine Passport' },
  { id: 'drivers_license', label: "Driver's License (LTO)" },
  { id: 'postal', label: 'Postal ID' },
  { id: 'umid', label: 'UMID (SSS)' },
  { id: 'prc', label: 'PRC ID' },
  { id: 'voters', label: "Voter's ID" },
  { id: 'philhealth', label: 'PhilHealth ID' },
  { id: 'other', label: 'Other government-issued ID' },
];

export const JUNIOR_PARENT_ID_TYPE_IDS = JUNIOR_PARENT_ID_TYPES.map((t) => t.id);

export const juniorParentIdTypeLabel = (id, otherLabel = '') => {
  const type = String(id || '').trim().toLowerCase();
  if (type === 'other') {
    const extra = String(otherLabel || '').trim();
    return extra ? `Other — ${extra}` : 'Other government-issued ID';
  }
  return JUNIOR_PARENT_ID_TYPES.find((t) => t.id === type)?.label || '';
};

/** True only when ID type is selected and a photo was actually uploaded (file_id). */
export const isJuniorParentIdComplete = (form, { partner = false } = {}) => {
  const prefix = partner ? 'partner_junior_parent_id' : 'junior_parent_id';
  const type = String(form?.[`${prefix}_type`] || '').trim().toLowerCase();
  if (!JUNIOR_PARENT_ID_TYPE_IDS.includes(type)) return false;
  if (type === 'other' && !String(form?.[`${prefix}_other`] || '').trim()) return false;
  return Boolean(String(form?.[`${prefix}_file_id`] || '').trim());
};

export const kidsJuniorParentIdBlocking = (tournament, form, { doubles = false } = {}) => {
  if (playerNeedsKidsParentId(tournament, form?.age) && !isJuniorParentIdComplete(form)) return true;
  if (doubles && playerNeedsKidsParentId(tournament, form?.partner_age)
    && !isJuniorParentIdComplete(form, { partner: true })) return true;
  return false;
};

export const kidsAgeAllowed = (age) => {
  const n = Number(age);
  return Number.isFinite(n) && n > 0 && n <= KIDS_MAX_AGE;
};

export const seniorsAgeAllowed = (age) => {
  const n = Number(age);
  return Number.isFinite(n) && n >= 50;
};

/** Dedicated Kids / Junior, or Open split and age 17 and under. */
export const playerNeedsKidsParentId = (tournament, age) => {
  if (isKidsTournament(tournament)) return true;
  if (tournamentRunsKidsSplit(tournament)) return kidsAgeAllowed(age);
  return false;
};

/** Same gate as parent ID: dedicated Kids, or Open split and age 17 and under. */
export const playerNeedsJuniorWaiver = (tournament, age) => playerNeedsKidsParentId(tournament, age);

/** Dedicated Senior / Seniors, or Open split and age 50+. */
export const playerNeedsSeniorWaiver = (tournament, age) => {
  if (isSeniorsTournament(tournament)) return true;
  if (tournamentRunsAgeSplit(tournament)) return seniorsAgeAllowed(age);
  return false;
};

export const stripAgeCategoryPrefix = (label) => {
  const text = String(label || '').trim();
  if (!text) return '';
  const stripped = text.replace(AGE_CATEGORY_PREFIX_RE, '').trim();
  return stripped || text;
};

/** Public category heading: Senior Men's Singles / Junior Mixed Doubles. */
export const publicCategoryLabel = (tournamentOrCategory, ageDivision) => {
  if (tournamentOrCategory && typeof tournamentOrCategory === 'object') {
    const t = tournamentOrCategory;
    const div = normalizeAgeDivision(ageDivision ?? t.age_division);
    const base = stripAgeCategoryPrefix(t.category_label) || categoryDisplayLabel(t.category);
    if (div === AGE_DIVISION_SENIORS) return `${SENIOR_CATEGORY_PREFIX} ${base}`;
    if (div === AGE_DIVISION_KIDS) return `${JUNIOR_CATEGORY_PREFIX} ${base}`;
    return base;
  }
  const base = categoryDisplayLabel(tournamentOrCategory);
  const div = normalizeAgeDivision(ageDivision);
  if (div === AGE_DIVISION_SENIORS) return `${SENIOR_CATEGORY_PREFIX} ${base}`;
  if (div === AGE_DIVISION_KIDS) return `${JUNIOR_CATEGORY_PREFIX} ${base}`;
  return base;
};

/** Player-facing division name: "Low Novice", "Advanced", "Kids / Junior". */
export const divisionDisplayLabel = (tournament) => {
  if (isKidsTournament(tournament)) return KIDS_DIVISION_LABEL;
  const base = normalizeSkillLevel(tournament?.skill_level);
  const prefix = skillTierLabel(tournament?.skill_tier);
  return prefix ? `${prefix} ${base}` : base;
};

/** Same category and base skill, opposite tier — the usual transfer destination. */
export const isSiblingTierTournament = (tournament, other) => {
  if (!tournament || !other) return false;
  if (other.tournament_id === tournament.tournament_id) return false;
  if (isKidsTournament(tournament) !== isKidsTournament(other)) return false;
  if (isSeniorsTournament(tournament) !== isSeniorsTournament(other)) return false;
  if (normalizeCategoryId(other.category) !== normalizeCategoryId(tournament.category)) return false;
  if (normalizeSkillLevel(other.skill_level) !== normalizeSkillLevel(tournament.skill_level)) return false;
  return normalizeSkillTier(other.skill_tier) !== normalizeSkillTier(tournament.skill_tier);
};

export const siblingTierTournaments = (list, tournament) =>
  (Array.isArray(list) ? list : []).filter((t) => isSiblingTierTournament(tournament, t));

/** Same event day / bundle, different category — hop between connected divisions. */
export const isSiblingCategoryTournament = (tournament, other) => {
  if (!tournament || !other) return false;
  if (other.tournament_id === tournament.tournament_id) return false;
  if (isDeletedTournament(other) || isDeletedTournament(tournament)) return false;
  const bundleId = String(tournament.bundle_id || '').trim();
  const sameBundle = bundleId && bundleId === String(other.bundle_id || '').trim();
  const sameEventDay = String(other.title || '').trim().toLowerCase() === String(tournament.title || '').trim().toLowerCase()
    && String(other.tournament_date || '').slice(0, 10) === String(tournament.tournament_date || '').slice(0, 10)
    && normalizeSkillLevel(other.skill_level) === normalizeSkillLevel(tournament.skill_level);
  if (!sameBundle && !sameEventDay) return false;
  return normalizeCategoryId(other.category) !== normalizeCategoryId(tournament.category);
};

export const siblingCategoryTournaments = (list, tournament) =>
  (Array.isArray(list) ? list : []).filter((t) => isSiblingCategoryTournament(tournament, t));

/** DUPR → skill bands: Novice ≤2.99, Intermediate 3.00–3.99, Advanced ≥4.00. */
export const DUPR_SKILL_BANDS = {
  Novice: { max: 2.99 },
  Intermediate: { min: 3.0, max: 3.99 },
  Advanced: { min: 4.0 },
};

/** Parse a DUPR rating string/number → finite number or null. */
export const parseDuprRating = (raw) => {
  if (raw == null || raw === '') return null;
  const cleaned = String(raw).trim().replace(/,/g, '');
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0 || n > 8) return null;
  return Math.round(n * 100) / 100;
};

/** Map DUPR rating → Novice | Intermediate | Advanced (never Open Single). */
export const skillFromDupr = (ratingOrRaw) => {
  const rating = typeof ratingOrRaw === 'number' ? ratingOrRaw : parseDuprRating(ratingOrRaw);
  if (rating == null) return null;
  if (rating <= 2.99) return 'Novice';
  if (rating <= 3.99) return 'Intermediate';
  return 'Advanced';
};

/**
 * Whether a DUPR rating is allowed for a division skill.
 * Open Single: no DUPR limit. Missing/unparseable rating fails for N/I/A.
 */
export const duprMatchesSkill = (ratingOrRaw, skill) => {
  const division = normalizeSkillLevel(skill, '');
  if (!division) return false;
  if (isOpenSingleSkill(division) || skillSkipsDupr(division)) return true;
  const derived = skillFromDupr(ratingOrRaw);
  return derived === division;
};

/**
 * Advisory only: split each DUPR band in half to hint Low vs High.
 * Never blocks — the organizer corrects placements with a transfer.
 */
export const DUPR_TIER_MIDPOINTS = { Novice: 2.5, Intermediate: 3.5, Advanced: 4.5 };

export const suggestedTierFromDupr = (ratingOrRaw) => {
  const rating = typeof ratingOrRaw === 'number' ? ratingOrRaw : parseDuprRating(ratingOrRaw);
  if (rating == null) return SKILL_TIER_NONE;
  const derived = skillFromDupr(rating);
  const midpoint = DUPR_TIER_MIDPOINTS[derived];
  if (midpoint == null) return SKILL_TIER_NONE;
  return rating < midpoint ? SKILL_TIER_LOW : SKILL_TIER_HIGH;
};

/**
 * Public registration: exact skill match + DUPR band match (Open Single skips DUPR band).
 * Kids / Junior events swap that for an age gate. The Low/High tier is deliberately
 * NOT gated — `suggestedTier` lets callers warn without blocking.
 * `options`: { tournament, age } — omit for the plain skill-only check.
 */
export const skillEligibility = (userSkill, tournamentSkill, duprRating = null, options = {}) => {
  const { tournament = null, age = null, duprRequired = true } = options || {};
  const player = normalizeSkillLevel(userSkill);
  const division = normalizeSkillLevel(tournamentSkill);
  const derivedSkill = skillFromDupr(duprRating);
  const suggestedTier = suggestedTierFromDupr(duprRating);
  const tier = normalizeSkillTier(tournament?.skill_tier);
  const tierMismatch = !!(tier && suggestedTier && tier !== suggestedTier);

  // Dedicated Kids / Senior events gate on age even for Open Single / Flex.
  if (isKidsTournament(tournament)) {
    const allowed = kidsAgeAllowed(age);
    return {
      status: allowed ? 'match' : 'mismatch',
      allowed,
      player,
      division,
      duprOk: true,
      derivedSkill,
      suggestedTier: SKILL_TIER_NONE,
      tierMismatch: false,
      reason: allowed ? null : 'kids_age',
    };
  }
  if (isSeniorsTournament(tournament)) {
    const allowed = seniorsAgeAllowed(age);
    if (!allowed) {
      return {
        status: 'mismatch',
        allowed: false,
        player,
        division,
        duprOk: true,
        derivedSkill,
        suggestedTier: SKILL_TIER_NONE,
        tierMismatch: false,
        reason: 'seniors_age',
      };
    }
    // Tournament skill "Senior" is 50+ only and skips DUPR (Novice + Seniors Age Division still matches DUPR).
    if (isSeniorSkill(tournament?.skill_level || tournamentSkill)) {
      return {
        status: 'match',
        allowed: true,
        player,
        division,
        duprOk: true,
        derivedSkill,
        suggestedTier: SKILL_TIER_NONE,
        tierMismatch: false,
        reason: null,
      };
    }
  }
  // Open Single takes all comers: no skill match, no DUPR band.
  if (isOpenSingleSkill(division) || isOpenSingleTournament(tournament)) {
    return {
      status: 'match',
      allowed: true,
      player,
      division,
      duprOk: true,
      derivedSkill,
      suggestedTier: SKILL_TIER_NONE,
      tierMismatch: false,
      reason: null,
    };
  }
  // Flex players (stored as Open Single) may enter any adult division as-is.
  // Effective DUPR off uses the same Flex path — no skill or DUPR band match.
  if (isFlexPlayer(player) || duprRequired === false) {
    return {
      status: 'match',
      allowed: true,
      player,
      division,
      duprOk: true,
      derivedSkill,
      suggestedTier: SKILL_TIER_NONE,
      tierMismatch: false,
      reason: null,
    };
  }
  if (player !== division) {
    return {
      status: 'mismatch',
      allowed: false,
      player,
      division,
      duprOk: true,
      derivedSkill,
      suggestedTier,
      tierMismatch,
      reason: 'skill',
    };
  }
  // Newbie / Junior / Senior / custom catalog skills skip the DUPR band.
  if (skillSkipsDupr(division)) {
    return {
      status: 'match',
      allowed: true,
      player,
      division,
      duprOk: true,
      derivedSkill,
      suggestedTier: SKILL_TIER_NONE,
      tierMismatch: false,
      reason: null,
    };
  }
  // Juniors on a 10–70 event skip the DUPR band — they often have no rating.
  if (tournamentRunsKidsSplit(tournament) && kidsAgeAllowed(age) && Number(age) >= 10) {
    return {
      status: 'match',
      allowed: true,
      player,
      division,
      duprOk: true,
      derivedSkill,
      suggestedTier,
      tierMismatch,
      reason: null,
    };
  }
  if (!duprMatchesSkill(duprRating, division)) {
    return {
      status: 'mismatch',
      allowed: false,
      player,
      division,
      duprOk: false,
      derivedSkill,
      suggestedTier,
      tierMismatch,
      reason: 'dupr',
    };
  }
  return {
    status: 'match',
    allowed: true,
    player,
    division,
    duprOk: true,
    derivedSkill,
    suggestedTier,
    tierMismatch,
    reason: null,
  };
};

/** Age-gate failures (Kids or Senior-only) belong on Profile, not skill review. */
export const isAgeEligibilityReason = (reason) => reason === 'kids_age' || reason === 'seniors_age';
export const skillEligibilityMessage = (eligibility, tournamentSkill) => {
  const division = normalizeSkillLevel(tournamentSkill || eligibility?.division);
  if (!eligibility || eligibility.allowed) return '';
  if (eligibility.reason === 'kids_age') {
    return `Kids / Junior is for players ${KIDS_MAX_AGE} and under. Add your age to your profile if this looks wrong.`;
  }
  if (eligibility.reason === 'seniors_age') {
    return `Senior events are for players ${MENS_DOUBLES_50PLUS_AGE} and over. Add your age to your profile if this looks wrong.`;
  }
  if (eligibility.reason === 'dupr') {
    const needed = duprBandCaption(division);
    const rating = eligibility.derivedSkill
      ? `Your DUPR maps to ${eligibility.derivedSkill} (${duprBandCaption(eligibility.derivedSkill) || 'see Profile'})`
      : 'Your DUPR rating could not be read (use a number like 3.25, not text)';
    return `${rating}. This ${division} event needs ${needed || division}. High/Low is a draw split — it does not change the DUPR band. Update the rating on Profile or join the matching ${eligibility.derivedSkill || 'skill'} event.`;
  }
  return `This ${division} event is only open to ${division} players (your level: ${playerSkillLabel(eligibility.player) || eligibility.player}). Your DUPR is accepted only after the profile skill matches.`;
};

/** Non-blocking nudge when a player's DUPR points at the other tier. */
export const tierSuggestionMessage = (eligibility, tournament) => {
  if (!eligibility?.tierMismatch) return '';
  const target = skillTierLabel(tournament?.skill_tier);
  const suggested = skillTierLabel(eligibility.suggestedTier);
  if (!target || !suggested) return '';
  const base = normalizeSkillLevel(tournament?.skill_level);
  return `Your DUPR suggests ${suggested} ${base}, but you can still join ${target} ${base}. High/Low is not a DUPR lock — the organizer can move you later.`;
};

export const DUPR_BAND_CAPTIONS = {
  Novice: 'DUPR 0.00–2.99',
  Intermediate: 'DUPR 3.00–3.99',
  Advanced: 'DUPR 4.00 and up',
};

export const duprBandCaption = (skill) => DUPR_BAND_CAPTIONS[normalizeSkillLevel(skill)] || '';

/**
 * Why a player cannot add / use DUPR on this event (all categories, including High Intermediate Women's).
 * High/Low is a draw split only. DUPR still follows Novice / Intermediate / Advanced.
 */
export const eventDuprJoinHint = (tournament, user = null, globalRequired) => {
  if (!tournament) return '';
  const siteFlag = globalRequired === false || globalRequired === true
    ? globalRequired
    : user?.dupr_required_global;
  if (user && effectiveDuprRequired(user, siteFlag) === false) {
    return `${FLEX_PLAYER_LABEL} path: DUPR is not required for you. You can join this adult event without a rating.`;
  }
  if (isKidsTournament(tournament) || isJuniorSkill(tournament?.skill_level)) {
    return 'Kids / Junior: DUPR is not required. Age 17 and under is the gate.';
  }
  if (isSeniorSkill(tournament?.skill_level)) {
    return 'Senior skill: DUPR is not required. Age 50+ is the gate.';
  }
  if (skillSkipsDupr(tournament?.skill_level)) {
    const skill = normalizeSkillLevel(tournament?.skill_level);
    return skill === NEWBIE_SKILL
      ? 'Newbie: DUPR is not required. First-time and developing players welcome.'
      : `${skill}: DUPR is not required.`;
  }
  if (isOpenSingleTournament(tournament) || isOpenSingleSkill(tournament?.skill_level)) {
    return 'Open Single: any DUPR is accepted. Paste a dashboard.dupr.com player link if you have one.';
  }
  const skill = normalizeSkillLevel(tournament?.skill_level);
  const band = duprBandCaption(skill);
  const tier = skillTierLabel(tournament?.skill_tier);
  const category = publicCategoryLabel(tournament) || 'This category';
  const bits = [
    `${category} ${tier ? `${tier} ` : ''}${skill} uses ${band}.`,
    'Paste a number (e.g. 3.25) and a dashboard.dupr.com/player link.',
  ];
  if (tier) {
    bits.push(`${tier} ${skill} is a draw split — it does not reject a valid ${skill} DUPR.`);
  }
  if (user) {
    const eligibility = skillEligibility(
      user.skill_level,
      skill,
      user.dupr_rating,
      {
        tournament,
        age: user.age,
        duprRequired: effectiveDuprRequired(user, user.dupr_required_global),
      },
    );
    if (!eligibility.allowed && eligibility.reason === 'dupr') {
      bits.push(
        `Your saved DUPR maps to ${eligibility.derivedSkill || 'an unread rating'}, so this ${skill} event will not accept it.`,
      );
    } else if (!eligibility.allowed && eligibility.reason === 'skill') {
      bits.push(
        `Your profile skill is ${playerSkillLabel(eligibility.player) || eligibility.player}, not ${skill}. Request a skill review on Profile.`,
      );
    }
  }
  return bits.join(' ');
};

/**
 * Distinct visual identity per skill so players can tell divisions apart at a glance.
 * Used by TournamentBanner + Tournament registration panel filters/badges.
 */
export const SKILL_LEVEL_THEMES = {
  Newbie: {
    id: 'Newbie',
    label: 'Newbie',
    short: 'NEW',
    hint: 'First-time tournament players',
    description: 'First-time and brand-new players — no DUPR needed. Learn the flow of a real event.',
    fg: '#0369a1',
    bg: '#e0f2fe',
    border: '#7dd3fc',
    solid: '#0284c7',
    onSolid: '#ffffff',
    chipIdle: 'bg-sky-50 text-sky-900 ring-sky-200',
    chipActive: 'bg-sky-600 text-white ring-sky-600 shadow-md shadow-sky-600/30',
    badge: 'bg-sky-500 text-white',
    badgeSoft: 'bg-sky-100 text-sky-900 ring-1 ring-sky-300',
    cardAccent: '#0ea5e9',
  },
  Novice: {
    id: 'Novice',
    label: 'Novice',
    short: 'NOV',
    hint: 'New & developing players',
    description: 'New & developing players — friendly rallies, learning the rules, and first-time tournament nerves welcome.',
    // Clear green — approachable / entry division
    fg: '#15803d',
    bg: '#dcfce7',
    border: '#86efac',
    solid: '#16a34a',
    onSolid: '#ffffff',
    chipIdle: 'bg-green-50 text-green-800 ring-green-200',
    chipActive: 'bg-green-600 text-white ring-green-600 shadow-md shadow-green-600/30',
    badge: 'bg-green-500 text-white',
    badgeSoft: 'bg-green-100 text-green-900 ring-1 ring-green-300',
    cardAccent: '#22c55e',
  },
  Intermediate: {
    id: 'Intermediate',
    label: 'Intermediate',
    short: 'INT',
    hint: 'Competitive club level',
    description: 'Competitive club level — consistent serves and returns, dinking battles, and steady court coverage.',
    // Warm amber — standout mid tier
    fg: '#b45309',
    bg: '#fef3c7',
    border: '#fcd34d',
    solid: '#d97706',
    onSolid: '#ffffff',
    chipIdle: 'bg-amber-50 text-amber-900 ring-amber-200',
    chipActive: 'bg-amber-500 text-white ring-amber-500 shadow-md shadow-amber-500/35',
    badge: 'bg-amber-500 text-white',
    badgeSoft: 'bg-amber-100 text-amber-950 ring-1 ring-amber-300',
    cardAccent: '#f59e0b',
  },
  Advanced: {
    id: 'Advanced',
    label: 'Advanced',
    short: 'ADV',
    hint: 'High-level tournament play',
    description: 'High-level tournament play — fast hands, strategic stacking, and experienced medal chasers.',
    // Deep rose — elite signal
    fg: '#9f1239',
    bg: '#ffe4e6',
    border: '#fb7185',
    solid: '#e11d48',
    onSolid: '#ffffff',
    chipIdle: 'bg-rose-50 text-rose-900 ring-rose-200',
    chipActive: 'bg-rose-600 text-white ring-rose-600 shadow-md shadow-rose-600/35',
    badge: 'bg-rose-600 text-white',
    badgeSoft: 'bg-rose-100 text-rose-950 ring-1 ring-rose-300',
    cardAccent: '#f43f5e',
  },
  'Open Single': {
    id: 'Open Single',
    label: 'Open Single',
    short: 'OPEN',
    hint: 'Open singles — all levels and ages',
    description: "Open singles — any skill and any age may enter. Flex players can also join every other division.",
    fg: '#1e3a8a',
    bg: '#dbeafe',
    border: '#93c5fd',
    solid: '#1e4b85',
    onSolid: '#ffffff',
    chipIdle: 'bg-sky-50 text-sky-900 ring-sky-200',
    chipActive: 'bg-[#1e4b85] text-white ring-[#1e4b85] shadow-md shadow-[#1e4b85]/35',
    badge: 'bg-[#1e4b85] text-white',
    badgeSoft: 'bg-sky-100 text-sky-950 ring-1 ring-sky-300',
    cardAccent: '#3b82f6',
  },
  [KIDS_DIVISION_LABEL]: {
    id: KIDS_DIVISION_LABEL,
    label: KIDS_DIVISION_LABEL,
    short: 'KIDS',
    hint: `Players ${KIDS_MAX_AGE} and under`,
    description: `Kids / Junior — players ${KIDS_MAX_AGE} and under. No DUPR rating required, just your age on file.`,
    // Bright violet — clearly not one of the adult skill divisions
    fg: '#6b21a8',
    bg: '#f3e8ff',
    border: '#d8b4fe',
    solid: '#9333ea',
    onSolid: '#ffffff',
    chipIdle: 'bg-purple-50 text-purple-900 ring-purple-200',
    chipActive: 'bg-purple-600 text-white ring-purple-600 shadow-md shadow-purple-600/35',
    badge: 'bg-purple-600 text-white',
    badgeSoft: 'bg-purple-100 text-purple-950 ring-1 ring-purple-300',
    cardAccent: '#a855f7',
  },
  [AGE_DIVISION_SENIORS]: {
    id: AGE_DIVISION_SENIORS,
    label: 'Seniors',
    short: '50+',
    hint: 'Players 50 and over',
    description: 'Seniors — players 50 and over. One 50+ tree; DUPR still follows the event skill.',
    fg: '#b45309',
    bg: '#fef3c7',
    border: '#fcd34d',
    solid: '#d97706',
    onSolid: '#ffffff',
    chipIdle: 'bg-amber-50 text-amber-900 ring-amber-200',
    chipActive: 'bg-amber-600 text-white ring-amber-600 shadow-md shadow-amber-600/35',
    badge: 'bg-amber-600 text-white',
    badgeSoft: 'bg-amber-100 text-amber-950 ring-1 ring-amber-300',
    cardAccent: '#f59e0b',
  },
};

const CUSTOM_SKILL_PALETTES = [
  {
    fg: '#6d28d9',
    bg: '#ede9fe',
    border: '#c4b5fd',
    solid: '#7c3aed',
    onSolid: '#ffffff',
    chipIdle: 'bg-violet-50 text-violet-900 ring-violet-200',
    chipActive: 'bg-violet-600 text-white ring-violet-600 shadow-md shadow-violet-600/30',
    badge: 'bg-violet-500 text-white',
    badgeSoft: 'bg-violet-100 text-violet-950 ring-1 ring-violet-300',
    cardAccent: '#8b5cf6',
  },
  {
    fg: '#be185d',
    bg: '#fce7f3',
    border: '#f9a8d4',
    solid: '#db2777',
    onSolid: '#ffffff',
    chipIdle: 'bg-pink-50 text-pink-900 ring-pink-200',
    chipActive: 'bg-pink-600 text-white ring-pink-600 shadow-md shadow-pink-600/30',
    badge: 'bg-pink-500 text-white',
    badgeSoft: 'bg-pink-100 text-pink-950 ring-1 ring-pink-300',
    cardAccent: '#ec4899',
  },
  {
    fg: '#0f766e',
    bg: '#ccfbf1',
    border: '#5eead4',
    solid: '#0d9488',
    onSolid: '#ffffff',
    chipIdle: 'bg-teal-50 text-teal-900 ring-teal-200',
    chipActive: 'bg-teal-600 text-white ring-teal-600 shadow-md shadow-teal-600/30',
    badge: 'bg-teal-500 text-white',
    badgeSoft: 'bg-teal-100 text-teal-950 ring-1 ring-teal-300',
    cardAccent: '#14b8a6',
  },
  {
    fg: '#c2410c',
    bg: '#ffedd5',
    border: '#fdba74',
    solid: '#ea580c',
    onSolid: '#ffffff',
    chipIdle: 'bg-orange-50 text-orange-900 ring-orange-200',
    chipActive: 'bg-orange-600 text-white ring-orange-600 shadow-md shadow-orange-600/30',
    badge: 'bg-orange-500 text-white',
    badgeSoft: 'bg-orange-100 text-orange-950 ring-1 ring-orange-300',
    cardAccent: '#f97316',
  },
  {
    fg: '#3730a3',
    bg: '#e0e7ff',
    border: '#a5b4fc',
    solid: '#4f46e5',
    onSolid: '#ffffff',
    chipIdle: 'bg-indigo-50 text-indigo-900 ring-indigo-200',
    chipActive: 'bg-indigo-600 text-white ring-indigo-600 shadow-md shadow-indigo-600/30',
    badge: 'bg-indigo-500 text-white',
    badgeSoft: 'bg-indigo-100 text-indigo-950 ring-1 ring-indigo-300',
    cardAccent: '#6366f1',
  },
];

export const customSkillTheme = (name) => {
  const label = String(name || '').trim() || 'Skill';
  let hash = 0;
  for (let i = 0; i < label.length; i += 1) {
    hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
  }
  const pal = CUSTOM_SKILL_PALETTES[hash % CUSTOM_SKILL_PALETTES.length];
  const short = label.length <= 4 ? label.toUpperCase() : `${label.slice(0, 3).toUpperCase()}.`;
  return {
    id: label,
    label,
    short,
    hint: `${label} division — no DUPR band`,
    description: `${label} is a custom club division. Low / High tiers are allowed; DUPR is not required.`,
    ...pal,
  };
};

export const getSkillLevelTheme = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === AGE_DIVISION_KIDS || raw === KIDS_DIVISION_LABEL.toLowerCase()) {
    return SKILL_LEVEL_THEMES[KIDS_DIVISION_LABEL];
  }
  if (raw === AGE_DIVISION_SENIORS || raw === 'senior') {
    return SKILL_LEVEL_THEMES[AGE_DIVISION_SENIORS];
  }
  const skill = normalizeSkillLevel(value, '');
  if (isJuniorSkill(skill)) return SKILL_LEVEL_THEMES[KIDS_DIVISION_LABEL];
  if (isSeniorSkill(skill)) return SKILL_LEVEL_THEMES[AGE_DIVISION_SENIORS];
  if (SKILL_LEVEL_THEMES[skill]) return SKILL_LEVEL_THEMES[skill];
  if (!skill) return SKILL_LEVEL_THEMES.Novice;
  return customSkillTheme(skill);
};

export const skillShortLabel = (value) => {
  const n = normalizeSkillLevel(value, '');
  if (n === 'Intermediate') return 'Int.';
  if (n === 'Open Single') return 'Open';
  return getSkillLevelTheme(n).short || n;
};

/** Theme for a whole event, so Kids / Seniors pick up their own colour instead of base skill. */
export const getTournamentDivisionTheme = (tournament) => {
  if (isKidsTournament(tournament)) return getSkillLevelTheme(AGE_DIVISION_KIDS);
  if (isSeniorsTournament(tournament)) return getSkillLevelTheme(AGE_DIVISION_SENIORS);
  return getSkillLevelTheme(tournament?.skill_level);
};

/**
 * Division pills for browse UIs: the four skills plus Kids / Junior (legacy).
 * Prefer AGE_DIVISION_FILTER_PILLS for Kids / Senior; this kids value still
 * matches leftover callers that stuffed age into the skill row.
 */
export const SKILL_FILTER_KIDS = AGE_DIVISION_KIDS;
/** Fallback pills when the catalog has not loaded. Live UIs use TournamentCatalogContext. */
export const SKILL_FILTER_PILLS = [...SKILL_LEVELS, SKILL_FILTER_KIDS];

export const skillFilterLabel = (value) => (
  value === SKILL_FILTER_KIDS ? KIDS_DIVISION_LABEL : normalizeSkillLevel(value)
);

/** Newbie, Novice, Intermediate, Advanced, and catalog extras may run Low/High. */
export const skillFilterSupportsTiers = (value) => (
  value !== SKILL_FILTER_KIDS
  && value !== 'all'
  && !!value
  && !isOpenSingleSkill(value)
  && !isJuniorSkill(value)
  && !isSeniorSkill(value)
);

/**
 * True when tournament skill matches a filter (null/All = any).
 * Open Single events surface under every skill pill, because every level may enter.
 * Dedicated Kids / Seniors still surface under built-in skill pills (age buckets,
 * not DUPR chips). Custom catalog skills stay exact-match only.
 * Pass `{ includeAgeDedicated: false }` for skill-pill count badges.
 */
export const matchesSkillFilter = (tournament, skillFilter, options = {}) => {
  const { includeAgeDedicated = true } = options;
  if (!skillFilter || skillFilter === 'all') return true;
  if (skillFilter === SKILL_FILTER_KIDS) return isKidsTournament(tournament);
  const filterSkill = normalizeSkillLevel(skillFilter, '');
  const builtInBrowse = Object.prototype.hasOwnProperty.call(SKILL_RANK, filterSkill)
    || isNewbieSkill(filterSkill);
  if (includeAgeDedicated && builtInBrowse && (isKidsTournament(tournament) || isSeniorsTournament(tournament))) {
    return true;
  }
  if (isOpenSingleTournament(tournament) && !isKidsTournament(tournament) && !isSeniorsTournament(tournament)) {
    return true;
  }
  return normalizeSkillLevel(tournament?.skill_level) === normalizeSkillLevel(skillFilter);
};

/** Age-division browse pills: mixed Open vs dedicated Junior / Senior events. */
export const AGE_DIVISION_FILTER_ALL = 'all';
export const AGE_DIVISION_FILTER_PILLS = [
  { id: AGE_DIVISION_FILTER_ALL, label: 'All', short: 'All' },
  { id: AGE_DIVISION_OPEN, label: 'Open', short: 'Open' },
  { id: AGE_DIVISION_KIDS, label: KIDS_DIVISION_LABEL, short: 'Junior' },
  { id: AGE_DIVISION_SENIORS, label: 'Seniors', short: 'Seniors' },
];

export const AGE_DIVISION_THEMES = {
  [AGE_DIVISION_OPEN]: {
    solid: '#0284c7',
    onSolid: '#ffffff',
    chipIdle: 'bg-sky-50 text-sky-900 ring-sky-200',
    chipActive: 'bg-sky-600 text-white ring-sky-600 shadow-md shadow-sky-600/35',
    hint: 'Mixed-age Open events — Under 18, Under 50, and 50+',
  },
  [AGE_DIVISION_KIDS]: SKILL_LEVEL_THEMES[KIDS_DIVISION_LABEL],
  [AGE_DIVISION_SENIORS]: SKILL_LEVEL_THEMES[AGE_DIVISION_SENIORS],
};

export const getAgeDivisionTheme = (value) => {
  const id = normalizeAgeDivision(value);
  return AGE_DIVISION_THEMES[id] || AGE_DIVISION_THEMES[AGE_DIVISION_OPEN];
};

export const ageDivisionFilterLabel = (value) => {
  const id = String(value || AGE_DIVISION_FILTER_ALL).trim().toLowerCase() || AGE_DIVISION_FILTER_ALL;
  const pill = AGE_DIVISION_FILTER_PILLS.find((p) => p.id === id);
  return pill?.label || 'All';
};

export const matchesAgeDivisionFilter = (tournament, filter) => {
  if (!filter || filter === AGE_DIVISION_FILTER_ALL) return true;
  if (filter === AGE_DIVISION_KIDS) return isKidsTournament(tournament);
  if (filter === AGE_DIVISION_SENIORS) return isSeniorsTournament(tournament);
  return normalizeAgeDivision(tournament?.age_division) === normalizeAgeDivision(filter);
};

/**
 * Start seniors / juniors on their dedicated pill. Guests and ages 18–49 stay on All.
 * Browse UIs must still let them switch to Open / All / the other division.
 */
export const suggestedAgeDivisionFilter = (age) => {
  const n = Number(age);
  if (!Number.isFinite(n) || n <= 0) return AGE_DIVISION_FILTER_ALL;
  if (kidsAgeAllowed(n)) return AGE_DIVISION_KIDS;
  if (seniorsAgeAllowed(n)) return AGE_DIVISION_SENIORS;
  return AGE_DIVISION_FILTER_ALL;
};

const ageDivisionAckKey = (tournamentId) => `pb-age-div-ack:${String(tournamentId || '')}`;

/** Homepage chrome (navbar / footer) — close tournament overlays so landing buttons stay clickable. */
export const LANDING_NAV_EVENT = 'pickleball:landing-nav';

export const emitLandingNav = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(LANDING_NAV_EVENT));
};

export const hasAckedAgeDivision = (tournamentId) => {
  if (!tournamentId) return false;
  try {
    return sessionStorage.getItem(ageDivisionAckKey(tournamentId)) === '1';
  } catch {
    return false;
  }
};

export const rememberAckedAgeDivision = (tournamentId) => {
  if (!tournamentId) return;
  try {
    sessionStorage.setItem(ageDivisionAckKey(tournamentId), '1');
  } catch {
    /* private / blocked storage */
  }
};

/** Tier sub-filter: 'all' keeps both tiers and untiered events. */
export const matchesTierFilter = (tournament, tierFilter) => {
  if (!tierFilter || tierFilter === 'all') return true;
  return normalizeSkillTier(tournament?.skill_tier) === normalizeSkillTier(tierFilter);
};

export const TIER_FILTER_PILLS = [
  { id: 'all', label: 'All' },
  { id: SKILL_TIER_LOW, label: 'Low' },
  { id: SKILL_TIER_HIGH, label: 'High' },
];

/** True when a listing is a Low or High draw split (not the whole division). */
export const tournamentHasSkillTier = (tournament) => {
  const tier = normalizeSkillTier(tournament?.skill_tier);
  return tier === SKILL_TIER_LOW || tier === SKILL_TIER_HIGH;
};

/**
 * Show High / Low pills only when this age division (and optional skill chip)
 * actually has at least one Low or High event. Kids / Senior events never do.
 */
export const ageDivisionHasTierSplits = (
  tournaments,
  ageFilter = AGE_DIVISION_FILTER_ALL,
  skillFilter = 'all',
) => {
  if (!Array.isArray(tournaments) || !tournaments.length) return false;
  return tournaments.some(
    (t) =>
      matchesAgeDivisionFilter(t, ageFilter)
      && matchesSkillFilter(t, skillFilter)
      && tournamentHasSkillTier(t),
  );
};

/** Banner / marketing title: "Low Novice Senior Men's Singles" */
export const bannerTournamentTitle = (tournament) => {
  const division = divisionDisplayLabel(tournament);
  const category = publicCategoryLabel(tournament);
  if (division && category) return `${division} ${category}`;
  return category || division || 'Tournament';
};
export const AGE_GROUPS = [
  AGE_GROUP_UNDER_18, '18-25', '26-35', '36-45', '46-55', '18-50', '10-70', '18-70', '50-70', AGE_GROUP_50_PLUS,
];
/** Age bands that already include players 46+ / 50+ (no extra age-group option). */
export const SENIOR_FRIENDLY_AGE_GROUPS = ['46-55', '18-50', '10-70', '18-70', '50-70', AGE_GROUP_50_PLUS];

/** Parse "18-70" / "18–70" / "50+" into min/max years, or null. */
export const parseAgeGroupRange = (ageGroup) => {
  const text = String(ageGroup || '').trim();
  if (text === AGE_GROUP_50_PLUS) return { min: 50, max: 120 };
  const m = text.match(/(\d+)\s*[-–]\s*(\d+)/);
  if (!m) return null;
  const min = Number(m[1]);
  const max = Number(m[2]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min, max };
};

export const isSeniorFriendlyAgeGroup = (ageGroup) => {
  const text = String(ageGroup || '').trim();
  if (SENIOR_FRIENDLY_AGE_GROUPS.includes(text)) return true;
  const range = parseAgeGroupRange(text);
  return !!(range && range.max >= 46);
};

/** true / false when both age and band parse; otherwise null. */
export const ageFitsAgeGroup = (age, ageGroup) => {
  const range = parseAgeGroupRange(ageGroup);
  const n = Number(age);
  if (!range || !Number.isFinite(n)) return null;
  return n >= range.min && n <= range.max;
};

/** Dropdown label: 0-17 → Under 18, 50+ → 50+. */
export const formatAgeGroupOptionLabel = (ageGroup) => {
  const raw = String(ageGroup || '').trim();
  if (raw === AGE_GROUP_UNDER_18 || raw === '0-17') return UNDER_18_DIVISION_LABEL;
  if (raw === AGE_GROUP_50_PLUS) return '50+';
  return raw.replace(/-/g, '–');
};

/** Human-readable age band, e.g. "Ages 18–70". */
export const formatAgeGroupLabel = (ageGroup) => {
  const raw = String(ageGroup || '').trim();
  if (!raw) return '';
  if (raw === AGE_GROUP_UNDER_18 || raw === '0-17') return UNDER_18_DIVISION_LABEL;
  if (raw === AGE_GROUP_50_PLUS) return '50+';
  return `Ages ${raw.replace(/-/g, '–')}`;
};

/**
 * Learn More destination: signed-in players go to Events; guests stay on the public listing.
 */
export const tournamentLearnMorePath = (tournamentId, { isLoggedIn } = {}) => {
  const id = String(tournamentId || '').trim();
  if (isLoggedIn) {
    return id ? `/events?highlight=${encodeURIComponent(id)}` : '/events';
  }
  return id ? `/tournaments?highlight=${encodeURIComponent(id)}` : '/tournaments';
};

export const TOURNAMENT_STATUSES = ['upcoming', 'active', 'completed', 'cancelled'];
export const ENDED_TOURNAMENT_STATUSES = ['completed', 'cancelled'];

export const isEndedTournament = (tournament) =>
  ENDED_TOURNAMENT_STATUSES.includes(String(tournament?.status || '').toLowerCase());

export const isDeletedTournament = (tournament) =>
  String(tournament?.status || '').toLowerCase() === 'deleted';

/** Registration rows use `tournament_status`; live events use `status`. */
export const isEndedTournamentRecord = (item) =>
  isEndedTournament(item) || isEndedTournament({ status: item?.tournament_status });

/**
 * Lifecycle status (organizer-controlled):
 * - upcoming  → before / until event day; registration can be open or closed
 * - active    → event day / in progress (Ongoing Tournament)
 * - completed → organizer ended the tournament (Tournament Ended)
 * - cancelled → called off
 *
 * Registration availability is separate: registration_closed flag OR past registration_deadline.
 */

/** Local calendar YYYY-MM-DD (avoids UTC off-by-one on date-only fields). */
export const todayLocalISODate = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Public listings stay visible through the day after the event, then drop. */
export const PUBLIC_DISPLAY_GRACE_DAYS = 2;

const addCalendarDaysIso = (isoDate, days) => {
  const raw = String(isoDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  const [y, m, d] = raw.split('-').map(Number);
  const next = new Date(y, m - 1, d + Number(days || 0));
  const yy = next.getFullYear();
  const mm = String(next.getMonth() + 1).padStart(2, '0');
  const dd = String(next.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

/** True when today is on or after tournament_date + PUBLIC_DISPLAY_GRACE_DAYS. */
export const isPastPublicDisplayWindow = (tournament) => {
  const date = String(tournament?.tournament_date || '').slice(0, 10);
  const hideFrom = addCalendarDaysIso(date, PUBLIC_DISPLAY_GRACE_DAYS);
  if (!hideFrom) return false;
  return todayLocalISODate() >= hideFrom;
};

/** Hide from banner / Events / listing / my-registrations. */
export const isHiddenFromPublic = (tournament) =>
  isEndedTournament(tournament)
  || isDeletedTournament(tournament)
  || isPastPublicDisplayWindow(tournament);

export const isHiddenFromPublicRecord = (item) =>
  isEndedTournamentRecord(item)
  || isDeletedTournament(item)
  || isDeletedTournament({ status: item?.tournament_status })
  || isPastPublicDisplayWindow(item);

/** True when registration_deadline date is before today. */
export const isRegistrationDeadlinePassed = (tournament) => {
  const deadline = String(tournament?.registration_deadline || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(deadline)) return false;
  return todayLocalISODate() > deadline;
};

/** True on or after the tournament calendar date. */
export const isTournamentDateReached = (tournament) => {
  const date = String(tournament?.tournament_date || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  return todayLocalISODate() >= date;
};

/**
 * Organizer walk-ins stay available after public registration closes,
 * until the tournament date (and while the event is not ended).
 */
export const canOrganizerAddPlayers = (tournament) => {
  if (!tournament) return false;
  const status = String(tournament.status || '').toLowerCase();
  if (['completed', 'cancelled'].includes(status)) return false;
  if (isTournamentDateReached(tournament)) return false;
  return true;
};

/**
 * Effective registration closed = organizer closed flag, past deadline,
 * full capacity (0 slots left), or tournament already ended/cancelled.
 */
export const isRegistrationEffectivelyClosed = (tournament) => {
  if (!tournament) return true;
  const status = String(tournament.status || '').toLowerCase();
  if (['completed', 'cancelled'].includes(status)) return true;
  if (tournament.registration_closed) return true;
  if (isRegistrationDeadlinePassed(tournament)) return true;
  if (getRemainingSlots(tournament) === 0) return true;
  return false;
};

/** Homepage / listing status badge for a tournament. */
export const getTournamentBannerStatus = (tournament) => {
  if (!tournament) {
    return { id: 'upcoming', label: 'Upcoming', emoji: '⚪', tone: 'slate' };
  }
  const status = String(tournament.status || '').toLowerCase();

  if (status === 'cancelled') {
    return { id: 'cancelled', label: 'Cancelled', emoji: '⛔', tone: 'rose' };
  }
  if (status === 'completed') {
    return { id: 'ended', label: 'Tournament Ended', emoji: '🏁', tone: 'slate' };
  }
  if (status === 'active') {
    // Event in progress — registration usually closed, but still show closed if applicable
    if (isRegistrationEffectivelyClosed(tournament)) {
      return { id: 'ongoing', label: 'Ongoing · Reg. Closed', emoji: '🔵', tone: 'sky' };
    }
    return { id: 'ongoing', label: 'Ongoing Tournament', emoji: '🔵', tone: 'sky' };
  }

  // upcoming / open / default — badge follows registration window + deadline
  if (isRegistrationEffectivelyClosed(tournament)) {
    return { id: 'closed', label: 'Registration Closed', emoji: '🔴', tone: 'rose' };
  }
  if (status === 'upcoming' || status === 'open' || !status) {
    return { id: 'open', label: 'Registration Open', emoji: '🟢', tone: 'emerald' };
  }
  return { id: 'upcoming', label: 'Upcoming', emoji: '⚪', tone: 'slate' };
};

export const isRegistrationOpen = (tournament) => {
  if (!tournament) return false;
  if (isRegistrationEffectivelyClosed(tournament)) return false;
  const status = String(tournament.status || '').toLowerCase();
  return status === 'upcoming' || status === 'active' || status === 'open' || !status;
};

/** Organizer-set registration capacity (players for singles, pairs for doubles). */
export const MIN_PARTICIPANTS = 2;
export const MAX_PARTICIPANTS = 16;
/** Default field size: doubles/mixed = 8 pairs; singles = 16 players. */
export const DEFAULT_DOUBLES_SLOTS = 8;
export const DEFAULT_SINGLES_SLOTS = 16;

export const clampParticipantLimit = (value, fallback = MAX_PARTICIPANTS) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(MAX_PARTICIPANTS, Math.max(MIN_PARTICIPANTS, Math.round(n)));
};

/** 8 and 16 can be knockout or round robin; every other size is round robin. */
export const OPTIONAL_KNOCKOUT_FIELD_SIZES = [8, 16];
/** Legacy slot-only knockout: missing draw_format on an 8-slot event stays knockout. */
export const KNOCKOUT_FIELD_SIZES = [8];
export const DRAW_FORMAT_ROUND_ROBIN = 'round_robin';
export const DRAW_FORMAT_KNOCKOUT = 'knockout';
export const RR_WINS_TO_QUALIFY = 4;
export const RR_LOSSES_TO_DQ = 4;
/** Games each entry may need before 4 wins or 4 losses (4+3). */
export const RR_GAMES_TO_FINISH = RR_WINS_TO_QUALIFY + RR_LOSSES_TO_DQ - 1;
/** Qualifying knockout after round robin: grows with 4-win qualifiers (2–16), not a fixed 4. */
export const RR_KO_SLOT_MAX = MAX_PARTICIPANTS;
/** @deprecated Use rrKnockoutSlotCount; kept as the old 4-seed default in copy fallbacks. */
export const RR_KO_SLOT_COUNT = 4;

/** How many KO seeds to show: 4-win qualifiers (and anyone already seated), capped by the RR field. */
export const rrKnockoutSlotCount = ({ qualifiedCount = 0, seatedCount = 0, fieldSize = 0 } = {}) => {
  const field = Math.max(0, Math.min(MAX_PARTICIPANTS, Number(fieldSize) || 0));
  const n = Math.max(0, Number(qualifiedCount) || 0, Number(seatedCount) || 0);
  if (field > 0) return Math.min(field, n);
  return Math.min(MAX_PARTICIPANTS, n);
};

/** Podium labels for the qualifying knockout (no 3rd when only two remain). */
export const rrKnockoutPlaceLabels = (slotCount) => {
  const n = Math.max(0, Number(slotCount) || 0);
  if (n <= 0) return [];
  if (n === 1) return ['1st'];
  if (n === 2) return ['1st', '2nd'];
  return ['1st', '2nd', '3rd'];
};

/** Short pairing hint on a seed tile (bye when the opponent is past the field). */
export const rrKnockoutSeedHint = (seed, slotCount) => {
  const n = Math.max(0, Number(slotCount) || 0);
  if (n < 2 || seed < 1) return '';
  const pair = openingPairings(n).find(([a, b]) => a === seed || b === seed);
  if (!pair) return '';
  const other = pair[0] === seed ? pair[1] : pair[0];
  if (other > n) return 'bye';
  return `plays seed ${other}`;
};

export const rrKnockoutSeedHeading = (slotCount) => {
  const n = Math.max(0, Number(slotCount) || 0);
  if (n <= 0) return 'Knockout seeds';
  if (n <= 2) return 'Knockout seeds (final 1v2)';
  const tree = n <= 4 ? 4 : n <= 8 ? 8 : 16;
  const byes = tree - n;
  const byeNote = byes > 0 ? `, ${byes} bye${byes === 1 ? '' : 's'}` : '';
  if (tree <= 4) return `Knockout seeds (${n} on a 4-draw${byeNote})`;
  if (tree <= 8) return `Knockout seeds (${n} on an 8-draw${byeNote})`;
  return `Knockout seeds (${n} on a 16-draw${byeNote})`;
};
export const RR_BYE_ID = '__bye__';
export const RR_COURT_COUNT = 4;
export const RR_GAME_MINUTES = 15;
export const RR_START_MINUTES = 8 * 60;
/** An 8-player pool fills all 4 courts in one round (smaller pools do not pad). */
export const RR_POSTER_POOL_SIZE = 8;

/** Slot-only legacy: 8 is knockout. Prefer tournamentUsesRoundRobin when a tournament exists. */
export const usesRoundRobin = (slotCount) => {
  const n = clampParticipantLimit(slotCount || 0);
  return n !== 8;
};

export const canChooseDrawFormat = (slotCount) => (
  OPTIONAL_KNOCKOUT_FIELD_SIZES.includes(clampParticipantLimit(slotCount || 0))
);

export const resolvedDrawFormat = (tournament, slotCount) => {
  const n = clampParticipantLimit(slotCount ?? tournament?.max_slots ?? 0);
  if (!OPTIONAL_KNOCKOUT_FIELD_SIZES.includes(n)) return DRAW_FORMAT_ROUND_ROBIN;
  const raw = String(tournament?.draw_format || '').trim().toLowerCase().replace(/[-\s]/g, '_');
  if (raw === DRAW_FORMAT_KNOCKOUT || raw === DRAW_FORMAT_ROUND_ROBIN) return raw;
  return n === 8 ? DRAW_FORMAT_KNOCKOUT : DRAW_FORMAT_ROUND_ROBIN;
};

export const tournamentUsesRoundRobin = (tournament, slotCount) => (
  resolvedDrawFormat(tournament, slotCount) === DRAW_FORMAT_ROUND_ROBIN
);

export const tournamentHasDrawProgress = (tournament) => {
  const rrHas = (rr) => (rr?.matches || []).some((m) => m?.winner_id);
  const koHas = (results) => {
    if (!results || typeof results !== 'object') return false;
    return Object.values(results).some((v) => {
      if (v == null || v === false || v === '') return false;
      if (typeof v === 'object') return Object.keys(v).length > 0;
      return true;
    });
  };
  return (
    rrHas(tournament?.round_robin)
    || rrHas(tournament?.round_robin_50plus)
    || koHas(tournament?.bracket_results)
    || koHas(tournament?.bracket_results_50plus)
  );
};

/** One pool under 10; two pools at 10–16 (5+5, 6+6, 8+8). 8-player RR is one pool. */
export const roundRobinPoolCount = (slotCount) => {
  const n = clampParticipantLimit(slotCount || 0);
  return n >= 10 ? 2 : 1;
};

export const roundRobinPoolSizes = (slotCount) => {
  const n = clampParticipantLimit(slotCount || 0);
  const pools = roundRobinPoolCount(n);
  if (pools <= 0) return [];
  if (pools === 1) return [n];
  const a = Math.floor(n / 2);
  return [a, n - a];
};

export const roundRobinPoolLabel = (index) => (index === 0 ? 'A' : 'B');

/** Pool seat code: A1, A5, B1… from a 0-based field index. */
export const roundRobinSlotCode = (slotIndex, slotCount) => {
  const sizes = roundRobinPoolSizes(slotCount);
  if (!sizes.length) return null;
  let remaining = Math.max(0, Number(slotIndex) || 0);
  for (let i = 0; i < sizes.length; i += 1) {
    if (remaining < sizes[i]) {
      return `${roundRobinPoolLabel(i)}${remaining + 1}`;
    }
    remaining -= sizes[i];
  }
  return null;
};

export const poolSeatCode = (poolId, indexInPool) =>
  `${String(poolId || 'A').slice(0, 1).toUpperCase()}${Math.max(0, Number(indexInPool) || 0) + 1}`;

export const roundRobinSlotRangeCaption = (slotCount) => {
  const sizes = roundRobinPoolSizes(slotCount);
  if (!sizes.length) return '';
  return sizes
    .map((size, i) => {
      const letter = roundRobinPoolLabel(i);
      return size <= 1 ? `${letter}1` : `${letter}1–${letter}${size}`;
    })
    .join(' and ');
};

/** Slot caption on the assigner / grid: A1–A5, B1–B5 (not Player 1). */
export const roundRobinSlotCaption = (slotIndex, slotCount, { doubles: _doubles = false } = {}) =>
  roundRobinSlotCode(slotIndex, slotCount);

export const roundRobinFormatTldr = (slotCount, { doubles = false, tournament } = {}) => {
  const n = clampParticipantLimit(slotCount || tournament?.max_slots || 0);
  const isRr = tournament != null
    ? tournamentUsesRoundRobin(tournament, n)
    : usesRoundRobin(n);
  if (!isRr) return null;
  const unit = doubles ? 'pairs' : 'players';
  const pools = roundRobinPoolCount(n);
  const poolNote = pools === 2 ? ' in two pools (A / B)' : ' in one pool';
  const places = rrKnockoutPlaceLabels(n).join(' / ') || '1st / 2nd / 3rd';
  return {
    title: `${n} ${unit}: round robin${poolNote} → qualifying knockout (${places})`,
    body: `This ${n}-slot field is round robin${poolNote} — seats ${roundRobinSlotRangeCaption(n) || 'A1…'} cycle on 4 courts until ${RR_WINS_TO_QUALIFY} wins or ${RR_LOSSES_TO_DQ} losses. Click a name to award a match. Drag names to change pairings. Players who reach ${RR_WINS_TO_QUALIFY} wins wait in a pool; ${RR_LOSSES_TO_DQ} losses is DQ. Seat those qualifiers on a knockout that sizes itself (${places}) — 2 play a final, 3–4 use semifinals, 5–8 use a 8-draw with byes, 9+ a 16-draw with byes.`,
  };
};

/** Live Bracket dialog widths — public RR is a player/schedule table, not the court grid. */
export const PUBLIC_BRACKET_DIALOG_WIDTH = {
  elimination: 'w-[min(96vw,1100px)] max-w-[1100px]',
  roundRobin: 'w-[min(96vw,880px)] max-w-[880px]',
  roundRobinTwoPool: 'w-[min(96vw,920px)] max-w-[920px]',
  expanded: 'w-[min(99vw,1920px)] max-w-[1920px]',
};

/** Drag-resize bounds for the public Live Bracket modal (px). */
export const PUBLIC_BRACKET_DIALOG_SIZE = {
  minWidth: 560,
  minHeight: 400,
  maxWidth: 1920,
  maxHeight: 1080,
};

/** Small corner grip — stays out of the Close button. */
export const PUBLIC_BRACKET_RESIZE_HANDLE_PX = 18;

export const clampPublicBracketDialogSize = (width, height) => {
  const vw = typeof window !== 'undefined' ? window.innerWidth : PUBLIC_BRACKET_DIALOG_SIZE.maxWidth;
  const vh = typeof window !== 'undefined' ? window.innerHeight : PUBLIC_BRACKET_DIALOG_SIZE.maxHeight;
  const maxW = Math.min(PUBLIC_BRACKET_DIALOG_SIZE.maxWidth, Math.floor(vw * 0.99));
  const maxH = Math.min(PUBLIC_BRACKET_DIALOG_SIZE.maxHeight, Math.floor(vh * 0.96));
  const minW = Math.min(PUBLIC_BRACKET_DIALOG_SIZE.minWidth, maxW);
  const minH = Math.min(PUBLIC_BRACKET_DIALOG_SIZE.minHeight, maxH);
  return {
    width: Math.round(Math.min(maxW, Math.max(minW, Number(width) || 0))),
    height: Math.round(Math.min(maxH, Math.max(minH, Number(height) || 0))),
  };
};

/** Public header line: "10 under 50 · 10 50+ / Senior slots" */
export const publicAgeSplitSlotCaption = (openSlots, seniorSlots, under18Slots = null) =>
  (under18Slots != null
    ? `${under18Slots} under 18 · ${openSlots} under 50 · ${seniorSlots} 50+ / Senior slots`
    : `${openSlots} under 50 · ${seniorSlots} 50+ / Senior slots`);

export const publicKidsSlotCaption = (slotCount) =>
  `${slotCount} ${UNDER_18_DIVISION_LABEL} slots`;

export const publicSeniorsSlotCaption = (slotCount) =>
  `${slotCount} 50+ / Senior slots`;

/** Compact Live Bracket header chips — one row beside the title, not stacked under it. */
export const PUBLIC_BRACKET_HEADER_CHIP =
  'inline-flex max-w-full shrink-0 items-center rounded-md px-2 py-0.5 text-[10px] font-semibold leading-snug';

/**
 * Split the RR tldr so phones can stack:
 * 10 pairs: round robin in two pools (A / B)
 * → qualifying knockout (1st / 2nd / 3rd)
 */
export const publicRoundRobinCaptionLines = (slotCount, { doubles = false, tournament } = {}) => {
  const tldr = roundRobinFormatTldr(slotCount, { doubles, tournament });
  if (!tldr?.title) return null;
  const arrow = ' → ';
  const at = tldr.title.indexOf(arrow);
  if (at < 0) {
    return { full: tldr.title, lead: tldr.title, knockout: null };
  }
  return {
    full: tldr.title,
    lead: tldr.title.slice(0, at),
    knockout: `→ ${tldr.title.slice(at + arrow.length)}`,
  };
};

/** Short legend for the public player-schedule table. */
export const PUBLIC_SCHEDULE_ABBREVIATIONS = [
  { abbr: 'Ct', meaning: 'Court' },
  { abbr: 'W / L', meaning: 'Win / Loss' },
  { abbr: 'DQ', meaning: 'Disqualified (4 losses)' },
  { abbr: 'Pool A / B', meaning: 'Bracket group' },
];

export const normalizeTournamentTitleKey = (title) =>
  String(title || '').trim().toLowerCase();

/**
 * View Bracket opens only the clicked event.
 * Same-title sibling categories (e.g. a 18-division bundle) stay off the chip grid.
 */
export const PUBLIC_BRACKET_SHOW_TITLE_SIBLINGS = false;

export const publicScheduleTableHeading = (tournament, { divisionLabel } = {}) =>
  [
    tournament?.title,
    tournament?.category_label,
    tournament ? divisionDisplayLabel(tournament) : '',
    divisionLabel,
  ].filter(Boolean).join(' · ');

/**
 * Pick a dialog width from the field size / format.
 * Public round robin is one name + schedule table (admin keeps the court grid).
 */
export const publicBracketDialogLayout = (tournament, { slotCount } = {}) => {
  const n = clampParticipantLimit(
    slotCount ?? tournament?.max_slots ?? tournament?.teams?.length ?? 0,
  );
  if (!tournamentUsesRoundRobin(tournament, n)) {
    return {
      tier: 'elimination',
      widthClass: PUBLIC_BRACKET_DIALOG_WIDTH.elimination,
      expandedWidthClass: PUBLIC_BRACKET_DIALOG_WIDTH.expanded,
    };
  }
  const pools = roundRobinPoolCount(n);
  if (pools === 2) {
    return {
      tier: 'roundRobinTwoPool',
      widthClass: PUBLIC_BRACKET_DIALOG_WIDTH.roundRobinTwoPool,
      expandedWidthClass: PUBLIC_BRACKET_DIALOG_WIDTH.expanded,
    };
  }
  return {
    tier: 'roundRobin',
    widthClass: PUBLIC_BRACKET_DIALOG_WIDTH.roundRobin,
    expandedWidthClass: PUBLIC_BRACKET_DIALOG_WIDTH.expanded,
  };
};

export const defaultSlotsForCategory = (categoryId) =>
  (isDoublesCategory(categoryId) ? DEFAULT_DOUBLES_SLOTS : DEFAULT_SINGLES_SLOTS);

/** Statuses that count toward public registered_count (aligned with backend). */
export const PUBLIC_REGISTERED_STATUSES = new Set(['pending', 'approved', 'withdrawal_requested']);

/**
 * Strip private participant/bracket payloads from a WebSocket tournament update
 * so landing pages can merge instantly without exposing PII.
 */
export const publicTournamentFromRealtime = (raw) => {
  if (!raw?.tournament_id) return null;

  const participants = Array.isArray(raw.participants) ? raw.participants : null;
  let registeredCount = Number(raw.registered_count);
  let registeredCountOpen = Number(raw.registered_count_open);
  let registeredCount50plus = Number(raw.registered_count_50plus);
  let registeredCountUnder18 = Number(raw.registered_count_under18);
  if (participants) {
    const active = participants.filter(
      (p) => PUBLIC_REGISTERED_STATUSES.has(String(p?.status || '').toLowerCase()),
    );
    registeredCount = active.length;
    registeredCountOpen = active.filter(
      (p) => effectiveBracketDivision(p, raw) === BRACKET_DIVISION_OPEN,
    ).length;
    registeredCount50plus = active.filter(
      (p) => effectiveBracketDivision(p, raw) === BRACKET_DIVISION_50PLUS,
    ).length;
    registeredCountUnder18 = active.filter(
      (p) => effectiveBracketDivision(p, raw) === BRACKET_DIVISION_UNDER18,
    ).length;
  } else if (!Number.isFinite(registeredCount)) {
    registeredCount = Number(raw.current_participants) || 0;
  }

  const img = raw.image_url || raw.image || null;
  const upcoming = raw.upcoming_image_url || raw.upcoming_image || null;

  return {
    tournament_id: raw.tournament_id,
    tournament_code: raw.tournament_code,
    title: raw.title,
    category: raw.category,
    category_label: raw.category_label,
    skill_level: normalizeSkillLevel(raw.skill_level),
    skill_tier: normalizeSkillTier(raw.skill_tier),
    age_division: normalizeAgeDivision(raw.age_division),
    age_group: raw.age_group,
    tournament_date: raw.tournament_date,
    start_time: raw.start_time,
    end_time: raw.end_time,
    entry_fee: raw.entry_fee,
    entry_fee_per_player: !!raw.entry_fee_per_player,
    created_at: raw.created_at || '',
    ...(typeof raw.early_bird_active === 'boolean'
      ? { early_bird_active: raw.early_bird_active }
      : {}),
    ...(raw.early_bird_ends_on ? { early_bird_ends_on: raw.early_bird_ends_on } : {}),
    ...(Number.isFinite(Number(raw.early_bird_slot_cap))
      ? { early_bird_slot_cap: Number(raw.early_bird_slot_cap) }
      : {}),
    ...(Number.isFinite(Number(raw.early_bird_slots_taken))
      ? { early_bird_slots_taken: Number(raw.early_bird_slots_taken) }
      : {}),
    ...(Number.isFinite(Number(raw.early_bird_slots_left))
      ? { early_bird_slots_left: Number(raw.early_bird_slots_left) }
      : {}),
    ...(Number.isFinite(Number(raw.effective_entry_fee))
      ? { effective_entry_fee: Number(raw.effective_entry_fee) }
      : {}),
    ...(Number.isFinite(Number(raw.regular_entry_fee))
      ? { regular_entry_fee: Number(raw.regular_entry_fee) }
      : {}),
    max_slots: raw.max_slots,
    current_participants: raw.current_participants || 0,
    registered_count: registeredCount,
    ...(Number.isFinite(Number(raw.pending_approval_count))
      ? { pending_approval_count: Number(raw.pending_approval_count) }
      : {}),
    ...(Number.isFinite(Number(raw.paid_pending_approval_count))
      ? { paid_pending_approval_count: Number(raw.paid_pending_approval_count) }
      : {}),
    ...(Number.isFinite(Number(raw.unsettled_player_count))
      ? { unsettled_player_count: Number(raw.unsettled_player_count) }
      : {}),
    ...(Number.isFinite(registeredCountOpen) ? { registered_count_open: registeredCountOpen } : {}),
    ...(Number.isFinite(registeredCount50plus)
      ? { registered_count_50plus: registeredCount50plus }
      : {}),
    ...(Number.isFinite(registeredCountUnder18)
      ? { registered_count_under18: registeredCountUnder18 }
      : {}),
    registration_deadline: raw.registration_deadline,
    registration_closed: !!raw.registration_closed,
    description: raw.description || '',
    tournament_type: raw.tournament_type,
    tournament_type_label: raw.tournament_type_label,
    status: raw.status,
    draw_format: resolvedDrawFormat(raw),
    venue_id: raw.venue_id || 'venue_camsur',
    venue_name: raw.venue_name || DEFAULT_TOURNAMENT_VENUE,
    venue_address: raw.venue_address || DEFAULT_TOURNAMENT_VENUE_ADDRESS,
    image: img,
    image_url: img,
    image_updated_at: raw.image_updated_at,
    image_storage: raw.image_storage,
    upcoming_image: upcoming,
    upcoming_image_url: upcoming,
    upcoming_image_updated_at: raw.upcoming_image_updated_at,
    upcoming_image_storage: raw.upcoming_image_storage,
    snapchat_qr: raw.snapchat_qr_url || raw.snapchat_qr || null,
    snapchat_qr_url: raw.snapchat_qr_url || raw.snapchat_qr || null,
    snapchat_qr_file_id: raw.snapchat_qr_file_id,
    snapchat_qr_updated_at: raw.snapchat_qr_updated_at,
    snapchat_qr_storage: raw.snapchat_qr_storage,
    // Prefer server flag; keep previous on merge if absent
    ...(typeof raw.bracket_ready === 'boolean' ? { bracket_ready: raw.bracket_ready } : {}),
    ...(typeof raw.bracket_ready_50plus === 'boolean'
      ? { bracket_ready_50plus: raw.bracket_ready_50plus }
      : {}),
    ...(raw.bracket_results && typeof raw.bracket_results === 'object'
      ? { bracket_results: raw.bracket_results }
      : {}),
    ...(raw.bracket_results_50plus && typeof raw.bracket_results_50plus === 'object'
      ? { bracket_results_50plus: raw.bracket_results_50plus }
      : {}),
    ...(raw.round_robin && typeof raw.round_robin === 'object'
      ? { round_robin: raw.round_robin }
      : {}),
    ...(raw.round_robin_50plus && typeof raw.round_robin_50plus === 'object'
      ? { round_robin_50plus: raw.round_robin_50plus }
      : {}),
    ...(Array.isArray(raw.teams) ? { teams: raw.teams } : {}),
    ...(Array.isArray(raw.teams_50plus) ? { teams_50plus: raw.teams_50plus } : {}),
    ...(raw.max_slots_50plus != null ? { max_slots_50plus: raw.max_slots_50plus } : {}),
  };
};

/** Merge a public-shaped tournament into a list (create/update; drop ended or past display window). */
export const mergePublicTournamentList = (prev, incoming) => {
  const next = publicTournamentFromRealtime(incoming);
  if (!next?.tournament_id) return prev || [];
  const list = Array.isArray(prev) ? prev : [];
  if (isHiddenFromPublic(next)) {
    return list.filter((t) => t.tournament_id !== next.tournament_id);
  }
  const idx = list.findIndex((t) => t.tournament_id === next.tournament_id);
  if (idx === -1) return [next, ...list];
  const merged = { ...list[idx], ...next };
  // Don't wipe bracket_ready when WS payload omitted it
  if (typeof next.bracket_ready !== 'boolean' && typeof list[idx].bracket_ready === 'boolean') {
    merged.bracket_ready = list[idx].bracket_ready;
  }
  const out = [...list];
  out[idx] = merged;
  return out;
};

export const getRemainingSlots = (tournament) => {
  const max = clampParticipantLimit(tournament?.max_slots || MAX_PARTICIPANTS);
  const used = Number(tournament?.registered_count ?? tournament?.current_participants ?? 0);
  return Math.max(0, max - (Number.isFinite(used) ? used : 0));
};

/** Public label: filled seats / capacity (e.g. 0/16). */
export const formatFilledSlots = (used, max) => {
  const m = clampParticipantLimit(max || MAX_PARTICIPANTS);
  const u = Math.max(0, Math.min(m, Number.isFinite(Number(used)) ? Number(used) : 0));
  return `${u}/${m}`;
};

/**
 * Counts of events in other age divisions while a non-All age filter is active.
 * Helps explain why Seniors / Kids look "missing" when Open is selected.
 */
export const ageDivisionHiddenSummary = (tournaments, activeFilter) => {
  const filter = String(activeFilter || AGE_DIVISION_FILTER_ALL);
  if (!filter || filter === AGE_DIVISION_FILTER_ALL) return null;
  const list = Array.isArray(tournaments) ? tournaments : [];
  const hidden = [];
  AGE_DIVISION_FILTER_PILLS.forEach((pill) => {
    if (pill.id === AGE_DIVISION_FILTER_ALL || pill.id === filter) return;
    const count = list.filter((t) => matchesAgeDivisionFilter(t, pill.id)).length;
    if (count > 0) hidden.push({ id: pill.id, label: pill.label, count });
  });
  if (!hidden.length) return null;
  return {
    activeId: filter,
    activeLabel: ageDivisionFilterLabel(filter),
    hidden,
    phrase: hidden.map((h) => `${h.count} ${h.label}`).join(' and '),
  };
};

/**
 * Soft note when a DUPR skill pill is active but Kids/Seniors remain listed by age.
 * Only when Age Division is All and at least one dedicated age event is present.
 */
export const skillFilterAgeDedicatedNote = (tournaments, skillFilter, ageDivisionFilter) => {
  if (!skillFilter || skillFilter === 'all' || skillFilter === SKILL_FILTER_KIDS) return null;
  if (ageDivisionFilter && ageDivisionFilter !== AGE_DIVISION_FILTER_ALL) return null;
  const list = Array.isArray(tournaments) ? tournaments : [];
  const hasKids = list.some(isKidsTournament);
  const hasSeniors = list.some(isSeniorsTournament);
  if (!hasKids && !hasSeniors) return null;
  return { message: 'Also listing Junior & Seniors (by age, not skill)' };
};

/**
 * Under 50 vs 50+ remaining slots when the event runs two age brackets.
 * Each cohort uses the same max_slots field size.
 */
/** Remaining slots for a Kids / Under 18 event (single bracket). */
export const getKidsSlotSummary = (tournament) => {
  if (!isKidsTournament(tournament)) return null;
  const max = clampParticipantLimit(tournament?.max_slots || MAX_PARTICIPANTS);
  const usedRaw = Number(tournament?.registered_count ?? tournament?.current_participants ?? 0);
  const used = Number.isFinite(usedRaw) ? Math.max(0, usedRaw) : 0;
  return {
    max,
    used,
    left: Math.max(0, max - used),
  };
};

/** Remaining slots for a dedicated Senior (50+) event (single bracket). */
export const getSeniorsSlotSummary = (tournament) => {
  if (!isSeniorsTournament(tournament)) return null;
  const max = clampParticipantLimit(tournament?.max_slots || MAX_PARTICIPANTS);
  const usedRaw = Number(tournament?.registered_count ?? tournament?.current_participants ?? 0);
  const used = Number.isFinite(usedRaw) ? Math.max(0, usedRaw) : 0;
  return {
    max,
    used,
    left: Math.max(0, max - used),
  };
};

export const getAgeSplitSlotSummary = (tournament) => {
  if (!tournamentRunsAgeSplit(tournament)) return null;
  const max = clampParticipantLimit(tournament?.max_slots || MAX_PARTICIPANTS);
  const max50 = clampParticipantLimit(tournament?.max_slots_50plus ?? tournament?.max_slots ?? MAX_PARTICIPANTS);
  const maxU18 = clampParticipantLimit(tournament?.max_slots_under18 ?? tournament?.max_slots ?? MAX_PARTICIPANTS);
  const openUsedRaw = Number(tournament?.registered_count_open);
  const seniorsUsedRaw = Number(tournament?.registered_count_50plus);
  const under18UsedRaw = Number(tournament?.registered_count_under18);
  const under50Used = Number.isFinite(openUsedRaw) ? Math.max(0, openUsedRaw) : 0;
  const seniorsUsed = Number.isFinite(seniorsUsedRaw) ? Math.max(0, seniorsUsedRaw) : 0;
  const under18Used = Number.isFinite(under18UsedRaw) ? Math.max(0, under18UsedRaw) : 0;
  const kidsSplit = tournamentRunsKidsSplit(tournament);
  return {
    max,
    max50,
    maxU18,
    under50Used,
    seniorsUsed,
    under18Used,
    under50Left: Math.max(0, max - under50Used),
    seniorsLeft: Math.max(0, max50 - seniorsUsed),
    under18Left: Math.max(0, maxU18 - under18Used),
    kidsSplit,
  };
};

/**
 * How full the field feels for clients.
 * Age-split events use the worst cohort (any full bracket → Full; else almost-full if any ≥80%).
 */
export const getSlotFillStatus = (tournament) => {
  if (!tournament) {
    return { id: 'open', label: 'Open seats', tone: 'emerald' };
  }
  if (isRegistrationEffectivelyClosed(tournament)) {
    return { id: 'full', label: 'Full', tone: 'rose' };
  }

  const kids = getKidsSlotSummary(tournament);
  if (kids) {
    if (kids.left <= 0) return { id: 'full', label: 'Full', tone: 'rose' };
    if (kids.max > 0 && kids.used / kids.max >= 0.8) {
      return { id: 'almost', label: 'Almost full', tone: 'amber' };
    }
    return { id: 'open', label: 'Open seats', tone: 'emerald' };
  }

  const seniors = getSeniorsSlotSummary(tournament);
  if (seniors) {
    if (seniors.left <= 0) return { id: 'full', label: 'Full', tone: 'rose' };
    if (seniors.max > 0 && seniors.used / seniors.max >= 0.8) {
      return { id: 'almost', label: 'Almost full', tone: 'amber' };
    }
    return { id: 'open', label: 'Open seats', tone: 'emerald' };
  }

  const split = getAgeSplitSlotSummary(tournament);
  if (split) {
    const cohorts = [
      { used: split.under50Used, max: split.max, left: split.under50Left },
      { used: split.seniorsUsed, max: split.max50, left: split.seniorsLeft },
    ];
    if (split.kidsSplit) {
      cohorts.push({ used: split.under18Used, max: split.maxU18, left: split.under18Left });
    }
    if (cohorts.some((c) => c.left <= 0)) {
      return { id: 'full', label: 'Full', tone: 'rose' };
    }
    if (cohorts.some((c) => c.max > 0 && c.used / c.max >= 0.8)) {
      return { id: 'almost', label: 'Almost full', tone: 'amber' };
    }
    return { id: 'open', label: 'Open seats', tone: 'emerald' };
  }

  const max = clampParticipantLimit(tournament?.max_slots || MAX_PARTICIPANTS);
  const usedRaw = Number(tournament?.registered_count ?? tournament?.current_participants ?? 0);
  const used = Number.isFinite(usedRaw) ? Math.max(0, usedRaw) : 0;
  const left = Math.max(0, max - used);
  if (left <= 0) return { id: 'full', label: 'Full', tone: 'rose' };
  if (max > 0 && used / max >= 0.8) {
    return { id: 'almost', label: 'Almost full', tone: 'amber' };
  }
  return { id: 'open', label: 'Open seats', tone: 'emerald' };
};

export const shortTournamentDescription = (tournament) => {
  const raw = String(tournament?.description || '').trim();
  if (raw) return raw.length > 140 ? `${raw.slice(0, 137)}…` : raw;
  const cat = tournament?.category_label || 'Pickleball';
  const skill = divisionDisplayLabel(tournament) || 'All levels';
  return `${cat} · ${skill}. Join the Camsur Pickleball Club tournament and compete with the community.`;
};

/** Standard 16-player single-elim R1 pairings (seed a vs seed b). */
export const SINGLES_R1_PAIRINGS = [
  [1, 16], [8, 9], [4, 13], [5, 12],
  [2, 15], [7, 10], [3, 14], [6, 11],
];

/** 8-player single-elim opening round (quarterfinals). */
export const SINGLES_QF_PAIRINGS = [
  [1, 8], [4, 5], [2, 7], [3, 6],
];

/** 4-draw opening round (semifinals). Unused seeds are BYEs. */
export const SINGLES_SF_PAIRINGS = [
  [1, 4], [2, 3],
];

/** 2-draw: championship only. */
export const SINGLES_FINAL_PAIRINGS = [
  [1, 2],
];

/** Knockout tree size: next power of 2 (2, 4, 8, or 16). */
export const bracketTreeSize = (slotCount) => {
  const n = clampParticipantLimit(slotCount || MAX_PARTICIPANTS);
  if (n <= 2) return 2;
  if (n <= 4) return 4;
  if (n <= 8) return 8;
  return 16;
};

/** BYE count when the field is smaller than the power-of-2 tree. */
export const bracketByeCount = (slotCount) => {
  const n = clampParticipantLimit(slotCount || MAX_PARTICIPANTS);
  return Math.max(0, bracketTreeSize(n) - n);
};

/** Opening-round pairings for the field (BYEs fill seeds above max_slots). */
export const openingPairings = (slotCount) => {
  const tree = bracketTreeSize(slotCount);
  if (tree <= 2) return SINGLES_FINAL_PAIRINGS;
  if (tree <= 4) return SINGLES_SF_PAIRINGS;
  if (tree <= 8) return SINGLES_QF_PAIRINGS;
  return SINGLES_R1_PAIRINGS;
};

/** Both seeds are in the field — a match that must actually be played. */
export const isPlayInPairing = (pairing, fieldSize) => {
  const [a, b] = pairing || [];
  const n = Number(fieldSize) || 0;
  return Number(a) > 0 && Number(b) > 0 && Number(a) <= n && Number(b) <= n;
};

/** One seed is past max_slots — that match is a BYE (hidden on the tree). */
export const isByePairing = (pairing, fieldSize) => {
  const [a, b] = pairing || [];
  const n = Number(fieldSize) || 0;
  if (!(Number(a) > 0 && Number(b) > 0)) return false;
  return Number(a) > n || Number(b) > n;
};

/** Opening pairings that are real matches (not player-vs-BYE). */
export const playInPairings = (slotCount) => {
  const n = clampParticipantLimit(slotCount || MAX_PARTICIPANTS);
  return openingPairings(n).filter((p) => isPlayInPairing(p, n));
};

/**
 * Short why-the-draw-looks-like-this copy for non-power-of-2 fields
 * (e.g. 10 slots → 16-draw, 6 byes; play-ins 8 vs 9 and 7 vs 10).
 * Same math for singles, doubles, mixed, and open singles.
 */
export const byeBracketTldr = (slotCount, { doubles = false } = {}) => {
  const n = clampParticipantLimit(slotCount || 0);
  const byes = bracketByeCount(n);
  if (byes <= 0) return null;
  const tree = bracketTreeSize(n);
  const playIns = playInPairings(n);
  const unit = doubles ? 'pairs' : 'players';
  const byeWord = byes === 1 ? 'bye' : 'byes';
  const matchWord = playIns.length === 1 ? 'match' : 'matches';
  const nextRound = tree === 16 ? 'quarterfinals' : tree === 8 ? 'semifinals' : 'the final';
  const playLabel = playIns.map(([a, b]) => `${a} vs ${b}`).join(', ') || 'none';
  const customNote = n === 10
    ? 'not an 8-draw and not a custom 10-box tree'
    : `not a custom ${n}-box tree`;
  return {
    title: `${n} ${unit} use a ${tree}-draw with ${byes} ${byeWord}`,
    body: (
      `Knockout only works at 2, 4, 8, or 16 spots, so a ${n}-slot field is a ${tree}-draw — ${customNote}. `
      + `Round 1 is only ${playIns.length} play-in ${matchWord} (${playLabel}). `
      + `Everyone else starts in the ${nextRound} because they have no first-round opponent `
      + `(a bye is an empty first-round slot, not an extra win).`
    ),
  };
};

/** Alias used by singles (and doubles knockout) trees. */
export const singlesOpeningPairings = openingPairings;

export const isDoublesCategory = (categoryId) => normalizeCategoryId(categoryId).includes('doubles');
export const isSinglesCategory = (categoryId) => !isDoublesCategory(categoryId);
export const isMixedCategory = (categoryId) => normalizeCategoryId(categoryId).includes('mixed');

/** Under 50 vs 50+ / Seniors trees in one tournament (all club categories). */
export const BRACKET_DIVISION_OPEN = 'open';
export const BRACKET_DIVISION_50PLUS = '50plus';
export const BRACKET_DIVISION_UNDER18 = 'under18';
export const MENS_DOUBLES_50PLUS_AGE = 50;
export const AGE_GROUP_10_70 = '10-70';
export const AGE_GROUP_50_70 = '50-70';
export const UNDER_50_DIVISION_LABEL = 'Under 50';
export const SENIOR_DIVISION_LABEL = 'Seniors (50+)';
export const VALID_BRACKET_DIVISIONS = [
  BRACKET_DIVISION_OPEN,
  BRACKET_DIVISION_50PLUS,
  BRACKET_DIVISION_UNDER18,
];

export const isAgeSplitCategory = (categoryId) => {
  const id = normalizeCategoryId(categoryId);
  if (TOURNAMENT_CATEGORIES.some((c) => c.id === id)) return true;
  return id.startsWith('custom_');
};

/** Alias: same Under 50 / 50+ split for mixed, doubles, singles, and open singles. */
export const isMensDoublesAgeSplit = isAgeSplitCategory;

/** Kids / Junior and Senior events run a single bracket; Open events split at 50. */
export const tournamentRunsAgeSplit = (tournament) => {
  if (isKidsTournament(tournament) || isSeniorsTournament(tournament)) return false;
  return isAgeSplitCategory(tournament?.category);
};

/** Open events add an Under 18 tree beside Under 50 and 50+. */
export const tournamentRunsKidsSplit = (tournament) => tournamentRunsAgeSplit(tournament);

export const eventShowsKidsParentIdNote = (tournament) =>
  isKidsTournament(tournament) || tournamentRunsKidsSplit(tournament);

export const eventShowsSeniorWaiverNote = (tournament) =>
  isSeniorsTournament(tournament) || tournamentRunsAgeSplit(tournament);

/**
 * Player-facing How Brackets Work copy (landing, signup, tournament wizard).
 * Admin TLDRs stay in roundRobinFormatTldr / byeBracketTldr.
 */
export const publicHowBracketsWork = (tournament = null) => {
  const kids = isKidsTournament(tournament);
  const seniors = isSeniorsTournament(tournament);
  const threeTrees = !!(tournament && (tournamentRunsKidsSplit(tournament) || tournamentRunsAgeSplit(tournament)));
  const rr = tournament ? tournamentUsesRoundRobin(tournament) : null;
  const n = clampParticipantLimit(tournament?.max_slots || 0);
  const seats = n ? (roundRobinSlotRangeCaption(n) || 'A1–A8') : 'A1–A8';
  const slotCaption = n ? `${n}-player field` : 'the listed field size';

  let ageBullet;
  let ageBody;
  if (kids) {
    ageBullet = `Kids / Junior: one ${UNDER_18_DIVISION_LABEL} bracket for ages ${KIDS_MAX_AGE} and under.`;
    ageBody = `This is a Junior event. Everyone ${KIDS_MAX_AGE} and under plays in a single ${UNDER_18_DIVISION_LABEL} bracket — not mixed with Under 50 or 50+.`;
  } else if (seniors) {
    ageBullet = `Senior: one 50+ bracket. Ages ${MENS_DOUBLES_50PLUS_AGE} and over only.`;
    ageBody = `This is a Senior event. Everyone ${MENS_DOUBLES_50PLUS_AGE} and over plays in a single 50+ / Seniors bracket — not mixed with Under 18 or Under 50.`;
  } else if (threeTrees) {
    ageBullet = 'Three age trees: Under 18 (10–17), Under 50 (18–49), and 50+.';
    ageBody = `This event runs three separate brackets. Ages 10–17 play ${UNDER_18_DIVISION_LABEL}. Ages 18–49 play Under 50. Ages 50 and up play 50+ / Seniors. Organizers place paid, approved names onto each tree — nothing auto-fills from registration alone.`;
  } else {
    ageBullet = 'Age decides your tree: Kids / Junior events are Under 18 only; other events use Under 18, Under 50, and 50+.';
    ageBody = `Kids / Junior leftover events keep a single ${UNDER_18_DIVISION_LABEL} bracket (ages ${KIDS_MAX_AGE} and under). Standard club events split into three trees: Under 18 (10–17), Under 50 (18–49), and 50+ / Seniors. You play the tree that matches your age.`;
  }

  let drawBullet;
  let drawBody;
  if (rr === true) {
    drawBullet = `Round robin on pool seats ${seats}, then a knockout that sizes to who qualifies.`;
    drawBody = `Paid and approved names are placed on pool seats ${seats}. You play until ${RR_WINS_TO_QUALIFY} wins (qualify) or ${RR_LOSSES_TO_DQ} losses (DQ). The knockout then sizes itself to who qualified: 2 play a final, 3–4 use semifinals, 5 or more use an 8- or 16-draw with byes. Organizers place names on seats — the grid does not auto-fill.`;
  } else if (rr === false) {
    const bye = n ? byeBracketTldr(n) : null;
    drawBullet = bye
      ? `Knockout on a ${bracketTreeSize(n)}-draw (byes fill leftover spots).`
      : 'Knockout: seeds play a 2 / 4 / 8 / 16 tree.';
    drawBody = `${bye
      ? bye.body
      : 'Knockout only works at 2, 4, 8, or 16 spots. Leftover spots are byes (empty first-round slots), not extra wins.'} Paid and approved names are placed on seeds by the organizer — the tree does not auto-fill.`;
  } else {
    drawBullet = 'Round robin (pool seats, 4 wins to qualify) or knockout (2/4/8/16 tree with byes).';
    drawBody = `Most fields use round robin: names go on pool seats (${seats} on an 8-player pool). Play until ${RR_WINS_TO_QUALIFY} wins or ${RR_LOSSES_TO_DQ} losses; qualifiers then play a knockout that sizes to how many advanced. An 8-player knockout field uses seeds instead of pool seats. Paid, approved names are placed by the organizer — the tree does not auto-fill.`;
  }

  const slotsBullet = 'Listed slots are the current field. If few people join, organizers may lower the size.';
  const slotsBody = `Capacity on the listing is the current ${slotCaption}. If few players join, organizers may reduce slots (they cannot go below who already paid). A smaller field can change pool size or add/remove byes. This is how the draw stays fair — not a promise that the field will shrink.`;

  return {
    title: tournament ? 'How this tournament works' : 'How tournament brackets work',
    bullets: [ageBullet, drawBullet, slotsBullet],
    sections: [
      { heading: 'Age brackets', body: ageBody },
      { heading: 'How the draw is settled', body: drawBody },
      { heading: 'Slots can change', body: slotsBody },
    ],
  };
};

/** Player-facing DUPR TLDR — signup, profile, and registration. */
export const publicDuprSystemGuide = () => ({
  title: 'How DUPR works here',
  tooltip: 'Novice 0.00–2.99 · Intermediate 3.00–3.99 · Advanced 4.00+. High/Low is a draw split, not a DUPR lock.',
  bullets: [
    'DUPR has three bands only: Novice 0.00–2.99, Intermediate 3.00–3.99, Advanced 4.00 and up.',
    'High / Low (for example High Intermediate Women’s) is a draw split. It does not change the DUPR band.',
    'Type a number like 3.25. Paste a dashboard.dupr.com player link — not a screenshot filename.',
    'Your profile skill must match the event. A Novice profile cannot enter Intermediate until you request a skill review.',
    'Kids / Junior and Senior skill skip DUPR. Open Single accepts any rating.',
  ],
  sections: [
    {
      heading: 'The three bands',
      body: 'Novice is 0.00–2.99. Intermediate is 3.00–3.99. Advanced is 4.00 and up. The site maps your number to one of these — never to High or Low.',
    },
    {
      heading: 'High Intermediate (and other High / Low events)',
      body: 'High Intermediate Women’s still uses Intermediate DUPR (3.00–3.99). A 3.20 rating is valid. The site may suggest Low Intermediate; that is a hint, not a block.',
    },
    {
      heading: 'If your rating will not save',
      body: 'Check the number is in the event’s band, the profile skill matches that event, and the link is a dashboard.dupr.com/player URL.',
    },
  ],
});

/**
 * Landing / Events guide for the Age Division pills (All · Open · Kids / Junior · Seniors).
 * Safe to show without a tournament — does not change filter matching.
 */
export const publicAgeDivisionGuide = () => ({
  title: 'Age divisions',
  bullets: [
    `Open: mixed ages — ${UNDER_18_DIVISION_LABEL}, Under 50, and 50+ each play their own tree.`,
    `Kids / Junior: ${UNDER_18_DIVISION_LABEL} only (ages ${KIDS_MAX_AGE} and under).`,
    `Seniors: ages ${MENS_DOUBLES_50PLUS_AGE} and over, one 50+ tree.`,
    'Pills only hide listings. You can switch to All, Open, Junior, or Seniors anytime.',
  ],
  sections: [
    {
      heading: 'Open',
      body: 'Most club events are Open. You still play the tree that matches your age: Under 18 (10–17), Under 50 (18–49), or 50+ / Seniors.',
    },
    {
      heading: 'Kids / Junior',
      body: `Dedicated Junior events are ${UNDER_18_DIVISION_LABEL} only. Ages ${KIDS_MAX_AGE} and under. DUPR is not required.`,
    },
    {
      heading: 'Seniors',
      body: `Dedicated Senior events are 50+ only. If your profile age is ${MENS_DOUBLES_50PLUS_AGE} or over, we start you on the Seniors pill so those events are in front. You can still tap All or Open and join those events if you are eligible.`,
    },
  ],
});

/** Short confirm shown before Register Here / Sign up so players know Open vs Junior vs Senior. */
export const publicAgeDivisionConfirm = (tournament) => {
  const div = tournament ? normalizeAgeDivision(tournament.age_division) : AGE_DIVISION_OPEN;
  const label = tournament ? (publicCategoryLabel(tournament) || 'This event') : 'This event';
  if (div === AGE_DIVISION_KIDS) {
    return {
      title: 'Confirm Kids / Junior event',
      headline: 'This is a Kids / Junior event',
      body: `${label} is ${UNDER_18_DIVISION_LABEL} only — ages ${KIDS_MAX_AGE} and under. Open and Senior listings stay on the Age Division filters if you want to browse those instead.`,
      confirmLabel: 'Continue to registration',
      division: AGE_DIVISION_KIDS,
    };
  }
  if (div === AGE_DIVISION_SENIORS) {
    return {
      title: 'Confirm Senior event',
      headline: 'This is a Senior event',
      body: `${label} is 50+ only. Open and Junior listings stay on the Age Division filters if you want to browse those instead.`,
      confirmLabel: 'Continue to registration',
      division: AGE_DIVISION_SENIORS,
    };
  }
  return {
    title: 'Confirm Open event',
    headline: 'This is an Open event',
    body: `${label} is mixed-age. ${UNDER_18_DIVISION_LABEL}, Under 50, and 50+ each play their own tree. Dedicated Junior or Senior events are on the other Age Division pills.`,
    confirmLabel: 'Continue to registration',
    division: AGE_DIVISION_OPEN,
  };
};

export const CLUB_ACCOUNT_TERMS = {
  title: 'Terms and tournament transparency',
  bullets: [
    'Juniors under 18 can create an account. Tournament divisions still follow age rules.',
    'You agree to follow club and tournament rules, and that the information you submit is true.',
    'Brackets, pool seats, and field size are explained before you enter an event. Organizers may resize a field if few players join.',
  ],
  sections: [
    {
      heading: 'Accounts',
      body: 'Anyone aged 5–100 can create a club account, including juniors under 18. DUPR is optional under 18. Tournament entry still follows that event’s age trees and skill rules.',
    },
    {
      heading: 'Play and liability',
      body: 'You agree to follow club court rules and tournament committee decisions. Participation is at your own risk. Ages submitted for events may be verified by organizers.',
    },
    {
      heading: 'How brackets work',
      body: 'Before you register for a tournament you will see how that event’s age trees, round robin or knockout, and slot counts work. Listed capacity is the current field and may be lowered if few people join.',
    },
  ],
};

/** Wide age bands or any category that runs a separate 50+ tree. Dedicated Senior events use the Senior title instead. */
export const tournamentWelcomesSeniors = (tournament) => {
  if (isKidsTournament(tournament) || isSeniorsTournament(tournament)) return false;
  return isSeniorFriendlyAgeGroup(tournament?.age_group) || tournamentRunsAgeSplit(tournament);
};

export const seniorJoinHint = (tournament) => {
  if (isKidsTournament(tournament) || isSeniorsTournament(tournament)) return '';
  if (tournamentRunsKidsSplit(tournament) || tournamentRunsAgeSplit(tournament)) {
    return 'Under 18, Under 50, and Seniors each play their own bracket';
  }
  if (isSeniorFriendlyAgeGroup(tournament?.age_group)) {
    return 'Seniors Open';
  }
  return '';
};

/** Who-can-join line for cards/summaries (age band + brackets when present). */
export const ageGroupCaption = (tournament) => {
  if (isKidsTournament(tournament)) {
    return `${UNDER_18_DIVISION_LABEL} · ages ${KIDS_MAX_AGE} and under · own bracket`;
  }
  if (isSeniorsTournament(tournament)) {
    return `Ages ${MENS_DOUBLES_50PLUS_AGE}+ · Seniors bracket only`;
  }
  if (isOpenSingleTournament(tournament) && !tournamentRunsKidsSplit(tournament)) {
    return 'All ages · all skill levels welcome';
  }
  const label = formatAgeGroupLabel(tournament?.age_group);
  if (tournamentRunsKidsSplit(tournament) || tournamentRunsAgeSplit(tournament)) {
    return label
      ? `${label} · Under 18, Under 50, and 50+ / Seniors brackets`
      : 'Under 18, Under 50, and 50+ / Seniors brackets';
  }
  return label;
};

/** True when this player's age may enter the event (juniors use Under 18 even on 18–70 bands). */
export const playerAgeFitsEvent = (age, tournament) => {
  if (!tournament) return null;
  if (isKidsTournament(tournament)) return kidsAgeAllowed(age);
  if (isSeniorsTournament(tournament)) return seniorsAgeAllowed(age);
  if (isOpenSingleTournament(tournament)) return true;
  if (tournamentRunsKidsSplit(tournament) && kidsAgeAllowed(age) && Number(age) >= 10) return true;
  return ageFitsAgeGroup(age, tournament?.age_group);
};

/** Logged-in player: whether their profile age fits this event's age group. */
export const playerAgeFitsHint = (user, tournament) => {
  const n = Number(user?.age);
  if (!Number.isFinite(n)) return '';
  if (isKidsTournament(tournament)) {
    return kidsAgeAllowed(n)
      ? `Your age (${n}) qualifies for Kids / Junior`
      : `Your age (${n}) is above the Kids / Junior limit of ${KIDS_MAX_AGE}`;
  }
  if (isSeniorsTournament(tournament)) {
    return seniorsAgeAllowed(n)
      ? `Your age (${n}) qualifies for Seniors (50+)`
      : `Your age (${n}) is under the Senior minimum of ${MENS_DOUBLES_50PLUS_AGE}`;
  }
  // Open Single has no age band to fall outside of.
  if (isOpenSingleTournament(tournament)) {
    return `Your age (${n}) qualifies — Open Single is open to every age`;
  }
  if (tournamentRunsKidsSplit(tournament) && kidsAgeAllowed(n) && n >= 10) {
    return `Your age (${n}) qualifies — you play in the ${UNDER_18_DIVISION_LABEL} bracket`;
  }
  const fits = ageFitsAgeGroup(n, tournament?.age_group);
  if (fits == null) return '';
  if (fits) {
    if (tournamentRunsAgeSplit(tournament) && n >= MENS_DOUBLES_50PLUS_AGE) {
      return `Your age (${n}) qualifies — you play in the 50+ / Seniors bracket`;
    }
    return `Your age (${n}) qualifies for this age group`;
  }
  const label = formatAgeGroupLabel(tournament?.age_group);
  return label
    ? `Your age (${n}) is outside ${label}`
    : `Your age (${n}) does not match this age group`;
};

export const publicBracketIsReady = (tournament) => {
  if (!tournament) return false;
  if (tournament.bracket_ready || tournament.bracket_ready_50plus || tournament.bracket_ready_under18) return true;
  const n = clampParticipantLimit(tournament.max_slots);
  if (tournamentUsesRoundRobin(tournament, n)) return true;
  if (tournamentRunsAgeSplit(tournament)) {
    const n50 = clampParticipantLimit(tournament.max_slots_50plus ?? tournament.max_slots);
    if (tournamentUsesRoundRobin(tournament, n50)) return true;
  }
  const named = (teams) => (Array.isArray(teams) ? teams : []).some(
    (t) => String(t?.players?.[0]?.name || '').trim(),
  );
  return named(tournament.teams) || named(tournament.teams_50plus) || named(tournament.teams_under18);
};

/** Age-only cohort (ignores organizer play-up / play-down override). */
export const naturalBracketDivision = (participant) => {
  const age = Number(participant?.age);
  if (Number.isFinite(age) && age <= KIDS_MAX_AGE) {
    return BRACKET_DIVISION_UNDER18;
  }
  if (Number.isFinite(age) && age >= MENS_DOUBLES_50PLUS_AGE) {
    return BRACKET_DIVISION_50PLUS;
  }
  return BRACKET_DIVISION_OPEN;
};

/** Effective cohort: organizer override, else primary player age. */
export const participantBracketDivision = (participant) => {
  const override = String(participant?.bracket_division || '').trim().toLowerCase();
  if (VALID_BRACKET_DIVISIONS.includes(override)) {
    return override;
  }
  return naturalBracketDivision(participant);
};

/** Under 18 is its own tree on Kids / Junior events and on Open 10–70 splits. */
export const effectiveBracketDivision = (participant, tournament) => {
  if (isKidsTournament(tournament)) return BRACKET_DIVISION_UNDER18;
  if (isSeniorsTournament(tournament)) return BRACKET_DIVISION_50PLUS;
  const div = participantBracketDivision(participant);
  if (div === BRACKET_DIVISION_UNDER18 && !tournamentRunsKidsSplit(tournament)) {
    return BRACKET_DIVISION_OPEN;
  }
  return div;
};

/** Participant IDs already placed on roster / pool / bracket seats. */
export const assignedParticipantIdsFromTeams = (teams) => {
  const ids = new Set();
  (Array.isArray(teams) ? teams : []).forEach((team) => {
    const pid = (team?.players || [])[0]?.participant_id;
    if (pid) ids.add(String(pid));
  });
  return ids;
};

/**
 * Paid & approved entries that still need a pool/bracket seat for this division.
 * Age-split events only count the matching Under 18 / Under 50 / 50+ tree.
 */
export const unseatedApprovedParticipants = (
  tournament,
  teams,
  division = BRACKET_DIVISION_OPEN,
) => {
  const participants = Array.isArray(tournament?.participants) ? tournament.participants : [];
  const multiTree = tournamentRunsAgeSplit(tournament) || tournamentRunsKidsSplit(tournament)
    || isKidsTournament(tournament) || isSeniorsTournament(tournament);
  const assigned = assignedParticipantIdsFromTeams(teams);
  return participants.filter((p) => {
    if (p.status !== 'approved' || !p.payment_verified) return false;
    if (multiTree && effectiveBracketDivision(p, tournament) !== division) return false;
    return !assigned.has(String(p.participant_id || ''));
  });
};

export const PARTICIPANT_DND_MIME = 'application/x-pickleball-participant';

export const defaultMensDoubles50PlusSlots = (tournament) =>
  clampParticipantLimit(tournament?.max_slots);

/**
 * Gender implied by the tournament division (Men's / Women's).
 * Unified Mixed Doubles: captain gender is free; partner is opposite (see partner helper).
 */
export const impliedGenderForCategory = (categoryId) => {
  const id = normalizeCategoryId(categoryId);
  if (isMixedCategory(id)) return null;
  // Check womens before mens — "womens_" contains the substring "mens_"
  if (id.includes('womens_')) return 'Female';
  if (id.includes('mens_')) return 'Male';
  return null;
};

/**
 * Partner gender for doubles.
 * Mixed Doubles: opposite of captainGender when provided; otherwise null until captain picks.
 * Men's/Women's Doubles: same gender as the division.
 */
export const impliedPartnerGenderForCategory = (categoryId, captainGender = null) => {
  const id = normalizeCategoryId(categoryId);
  if (!isDoublesCategory(id)) return null;
  if (isMixedCategory(id)) {
    if (captainGender === 'Male') return 'Female';
    if (captainGender === 'Female') return 'Male';
    return null;
  }
  return impliedGenderForCategory(id);
};

export const tournamentFormatLabel = (categoryId, storedLabel) => {
  if (storedLabel) return storedLabel;
  return isSinglesCategory(categoryId) ? 'Singles Elimination' : 'Bracket / Round Robin';
};

export const categoryFilterLabel = (categoryId) => {
  const normalized = normalizeCategoryId(categoryId);
  const cat = allTournamentCategories().find((c) => c.id === normalized);
  return cat?.filterLabel || cat?.label || normalized;
};

export const categoryDisplayLabel = (categoryId) => {
  const normalized = normalizeCategoryId(categoryId);
  const cat = allTournamentCategories().find((c) => c.id === normalized);
  return cat?.label || normalized;
};

export const GENDER_OPTIONS = ['Male', 'Female'];

export const PAYMENT_METHODS = [
  { id: 'gcash', label: 'GCash' },
  { id: 'maya', label: 'Maya' },
  { id: 'bank_transfer', label: 'Bank Transfer' },
  { id: 'cash', label: 'Cash' },
];

export const REGISTRATION_WIZARD_STEPS = [
  { id: 'tournament', label: 'Tournament' },
  { id: 'player', label: 'Player Information' },
  { id: 'payment', label: 'Payment' },
  { id: 'review', label: 'Review' },
  { id: 'complete', label: 'Complete' },
];

/** DOM ids for autofocus on empty Player Information fields. */
export const REGISTRATION_PLAYER_FIELD_IDS = {
  full_name: 'reg-wizard-full-name',
  gender: 'reg-wizard-gender',
  age: 'reg-wizard-age',
  email: 'reg-wizard-email',
  contact_number: 'reg-wizard-contact-number',
  partner_full_name: 'reg-wizard-partner-full-name',
  partner_gender: 'reg-wizard-partner-gender',
  partner_age: 'reg-wizard-partner-age',
  partner_contact_number: 'reg-wizard-partner-contact-number',
  playing_experience: 'reg-wizard-playing-experience',
  previous_winner: 'reg-wizard-previous-winner',
  previous_winner_details: 'reg-wizard-previous-winner-details',
  previous_winner_proof: 'reg-wizard-previous-winner-proof',
  senior_waiver_file_id: 'reg-wizard-senior-waiver',
  partner_senior_waiver_file_id: 'reg-wizard-partner-senior-waiver',
  junior_waiver_file_id: 'reg-wizard-junior-waiver',
  partner_junior_waiver_file_id: 'reg-wizard-partner-junior-waiver',
  junior_parent_id_type: 'reg-wizard-junior-parent-id-type',
  junior_parent_id_other: 'reg-wizard-junior-parent-id-other',
  junior_parent_id_file_id: 'reg-wizard-junior-parent-id-file',
  partner_junior_parent_id_type: 'reg-wizard-partner-junior-parent-id-type',
  partner_junior_parent_id_other: 'reg-wizard-partner-junior-parent-id-other',
  partner_junior_parent_id_file_id: 'reg-wizard-partner-junior-parent-id-file',
  dupr_rating: 'reg-wizard-dupr-rating',
  dupr_profile_url: 'reg-wizard-dupr-profile-url',
  tshirt_size: 'reg-wizard-tshirt-size',
  partner_tshirt_size: 'reg-wizard-partner-tshirt-size',
};

/** How long have you been playing Pickleball? */
export const PLAYING_EXPERIENCE_OPTIONS = [
  { id: 'less_than_6_months', label: 'Less than 6 months' },
  { id: '6_months_to_1_year', label: '6 months – 1 year' },
  { id: '1_to_2_years', label: '1–2 years' },
  { id: 'more_than_2_years', label: 'More than 2 years' },
];

/** Waiver & Agreement checkboxes (shared by public + admin registration). */
export const WAIVER_AGREEMENTS = [
  {
    key: 'waiver_certified',
    label: 'I certify that all information provided is true and correct.',
  },
  {
    key: 'waiver_age_verify',
    label: 'I understand that the age(s) submitted will be verified by tournament administrators.',
  },
  {
    key: 'waiver_rules',
    label: 'I agree to follow all tournament rules and decisions of the Tournament Committee.',
  },
  {
    key: 'waiver_liability',
    label:
      'I understand that participation is at my own risk and release the organizers from any liability for injuries or accidents.',
  },
];

export const WAIVER_FIELD_KEYS = WAIVER_AGREEMENTS.map((w) => w.key);

export const SNAPCHAT_GROUP_ACK_LABEL =
  'I joined the Snapchat group for this tournament.';

export const GROUP_CHAT_ACK_LABEL =
  'I joined the group chat for this tournament.';

export const GROUP_CHAT_URL_MAX_LEN = 2000;

export const normalizeGroupChatUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.length > GROUP_CHAT_URL_MAX_LEN) return '';
  const lower = raw.toLowerCase();
  if (!lower.startsWith('http://') && !lower.startsWith('https://')) return '';
  return raw;
};

export const tournamentHasGroupChat = (tournament) => Boolean(
  normalizeGroupChatUrl(tournament?.group_chat_url),
);

export const SENIOR_WAIVER_PDF_HREF = '/docs/CPC_WAIVER_50_ABOVE.pdf';
export const JUNIOR_WAIVER_PDF_HREF = '/docs/CPC_WAIVER_18_UNDER.pdf';
export const SENIOR_WAIVER_MAX_BYTES = 10 * 1024 * 1024;
export const JUNIOR_WAIVER_MAX_BYTES = SENIOR_WAIVER_MAX_BYTES;
export const SENIOR_WAIVER_ACCEPT = [
  '.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg', 'image/png', 'image/webp',
].join(',');
export const JUNIOR_WAIVER_ACCEPT = SENIOR_WAIVER_ACCEPT;

export const requiresSeniorWaiver = (tournament, age) => {
  if (age !== undefined && age !== null && String(age).trim() !== '') {
    return playerNeedsSeniorWaiver(tournament, age);
  }
  return eventShowsSeniorWaiverNote(tournament);
};

export const isSeniorWaiverComplete = (form, { partner = false } = {}) => {
  const prefix = partner ? 'partner_senior_waiver' : 'senior_waiver';
  return Boolean(String(form?.[`${prefix}_file_id`] || '').trim());
};

export const seniorWaiverBlocking = (tournament, form, { doubles = false } = {}) => {
  if (playerNeedsSeniorWaiver(tournament, form?.age) && !isSeniorWaiverComplete(form)) return true;
  if (doubles && playerNeedsSeniorWaiver(tournament, form?.partner_age)
    && !isSeniorWaiverComplete(form, { partner: true })) return true;
  return false;
};

export const isJuniorWaiverComplete = (form, { partner = false } = {}) => {
  const prefix = partner ? 'partner_junior_waiver' : 'junior_waiver';
  return Boolean(String(form?.[`${prefix}_file_id`] || '').trim());
};

export const juniorWaiverBlocking = (tournament, form, { doubles = false } = {}) => {
  if (playerNeedsJuniorWaiver(tournament, form?.age) && !isJuniorWaiverComplete(form)) return true;
  if (doubles && playerNeedsJuniorWaiver(tournament, form?.partner_age)
    && !isJuniorWaiverComplete(form, { partner: true })) return true;
  return false;
};

export const resolveSnapchatQrUrl = (tournament, apiBase = '') => {
  const absolute = tournament?.snapchat_qr_url || tournament?.snapchat_qr;
  if (typeof absolute === 'string' && absolute.startsWith('http')) {
    if (tournament?.snapchat_qr_storage === 's3' && tournament?.snapchat_qr_updated_at) {
      const sep = absolute.includes('?') ? '&' : '?';
      return `${absolute}${sep}v=${encodeURIComponent(tournament.snapchat_qr_updated_at)}`;
    }
    return absolute;
  }
  const fileId = tournament?.snapchat_qr_file_id;
  const base = String(apiBase || '').replace(/\/$/, '');
  if (fileId && base) return `${base}/tournaments/images/${fileId}`;
  if (typeof absolute === 'string' && absolute.startsWith('/')) return absolute;
  return '';
};

export const tournamentHasSnapchatGroup = (tournament) => Boolean(
  tournament?.snapchat_qr_url
  || tournament?.snapchat_qr
  || tournament?.snapchat_qr_file_id,
);

export const playingExperienceLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const byId = PLAYING_EXPERIENCE_OPTIONS.find((o) => o.id === raw);
  if (byId) return byId.label;
  const byLabel = PLAYING_EXPERIENCE_OPTIONS.find((o) => o.label === raw);
  if (byLabel) return byLabel.label;
  return raw;
};

export const defaultParticipantForm = (entryFee = 0) => ({
  full_name: '',
  nickname: '',
  date_of_birth: '',
  age: '',
  gender: '',
  city: '',
  contact_number: '',
  email: '',
  partner_full_name: '',
  partner_gender: '',
  partner_age: '',
  partner_contact_number: '',
  partner_email: '',
  emergency_contact_name: '',
  emergency_relationship: '',
  emergency_contact_number: '',
  playing_experience: '',
  previous_winner: false,
  previous_winner_details: '',
  previous_winner_proof_url: '',
  previous_winner_proof_filename: '',
  previous_winner_proof_file_id: '',
  junior_parent_id_type: '',
  junior_parent_id_other: '',
  junior_parent_id_file_id: '',
  junior_parent_id_url: '',
  junior_parent_id_filename: '',
  partner_junior_parent_id_type: '',
  partner_junior_parent_id_other: '',
  partner_junior_parent_id_file_id: '',
  partner_junior_parent_id_url: '',
  partner_junior_parent_id_filename: '',
  senior_waiver_file_id: '',
  senior_waiver_url: '',
  senior_waiver_filename: '',
  partner_senior_waiver_file_id: '',
  partner_senior_waiver_url: '',
  partner_senior_waiver_filename: '',
  junior_waiver_file_id: '',
  junior_waiver_url: '',
  junior_waiver_filename: '',
  partner_junior_waiver_file_id: '',
  partner_junior_waiver_url: '',
  partner_junior_waiver_filename: '',
  dupr_rating: '',
  dupr_profile_url: '',
  player_skill_level: '',
  played_up: false,
  tshirt_size: '',
  partner_tshirt_size: '',
  waiver_certified: false,
  waiver_age_verify: false,
  waiver_rules: false,
  waiver_liability: false,
  registration_fee: entryFee,
  additional_fees: 0,
  payment_method: '',
  payment_reference: '',
  payment_proof_url: '',
  payment_verified: false,
  age_verified: false,
  notes: '',
});

/** Normalize PH mobile to 09XXXXXXXXX digits for registration inputs. */
export const normalizePhMobileDigits = (value) => {
  let cleaned = String(value || '').replace(/\D/g, '');
  if (cleaned.startsWith('63') && cleaned.length >= 12) {
    cleaned = `0${cleaned.slice(2)}`;
  }
  if (!cleaned.startsWith('0') && cleaned.length === 10) {
    cleaned = `0${cleaned}`;
  }
  return cleaned.slice(0, 11);
};

/** Prefill wizard fields from a logged-in user profile when available. */
export const prefillFromUser = (form, user) => {
  if (!user) return form;
  const ageValue =
    form.age !== '' && form.age != null
      ? form.age
      : (user.age != null && user.age !== '' ? String(user.age) : '');
  const phoneFromUser = normalizePhMobileDigits(user.phone);
  const phoneOk = /^09\d{9}$/.test(phoneFromUser);
  const savedDupr = parseDuprRating(user.dupr_rating);
  const savedDuprUrl = normalizeDuprProfileUrl(user.dupr_profile_url);
  return {
    ...form,
    full_name: form.full_name || user.name || '',
    email: form.email || user.email || '',
    contact_number: form.contact_number || (phoneOk ? phoneFromUser : ''),
    gender: form.gender || user.gender || '',
    age: ageValue,
    player_skill_level: form.player_skill_level || normalizeSkillLevel(user.skill_level),
    dupr_rating: form.dupr_rating || (savedDupr != null ? String(user.dupr_rating).trim() : ''),
    dupr_profile_url: form.dupr_profile_url || savedDuprUrl || '',
  };
};

/** localStorage key — first-time Flex skill-review hint (append user_id) */
export const flexSkillReviewHintKey = (userId) =>
  `pickleball_flex_skill_review_hint_v1_${userId || 'anon'}`;

/** localStorage key — first-time End Tournament walkthrough */
export const END_TOURNAMENT_TOUR_KEY = 'pickleball_end_tournament_tour_seen_v1';

/** localStorage key — first-time round-robin schedule table walkthrough */
export const RR_SCHEDULE_TOUR_KEY = 'pickleball_rr_schedule_tour_seen_v1';

export const RR_SCHEDULE_TOUR_STEPS = [
  {
    title: 'Seats A1–A5 / B1–B5',
    body: 'Each pool seat has a code (A1–A5 and B1–B5 on a 10-slot field, or A1… in one pool). Player names sit on those seats in the grid and standings.',
  },
  {
    title: 'Select with vs',
    body: 'Click vs on each match you want to move. Selected cells get a yellow highlight. Click vs again to unselect.',
  },
  {
    title: 'Allocate a time',
    body: 'Pick a time in Allocate selected and send the highlighted matches there, even if that row already has games. Or click a TBD court to drop them onto that slot.',
  },
  {
    title: 'Drag to move or swap',
    body: 'The Time column stays in clock order. Drag a match or player onto a TBD court to place it in that row, or onto another match to swap. Drag a Court header to move that whole column to another court. Times do not move with the names.',
  },
  {
    title: 'Click a name to score',
    body: 'Click a player or pair name to award that match. Click the winner again to undo. 4 wins qualify; 4 losses is DQ.',
  },
];

export const DEFAULT_TOURNAMENT_VENUE = 'CamSur Pickleball Club';
export const DEFAULT_TOURNAMENT_VENUE_ADDRESS =
  'CamSur Pickleball Club, Capitol Complex, Cadlan, Pili, Camarines Sur.';

/** Official DUPR signup — for players who do not have a rating yet. */
export const DUPR_SIGNUP_URL = 'https://dashboard.dupr.com/signup';

/**
 * Extract and normalize a DUPR profile URL from raw paste
 * (plain URL, player id, or a message that contains the link).
 * Accepts: https://dashboard.dupr.com/dashboard/player/4965412923
 * and the same path with optional /profile suffix.
 */
export const normalizeDuprProfileUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  // Prefer first http(s) URL in pasted text
  const httpMatch = raw.match(/https?:\/\/[^\s<>"']+/i);
  // Or a bare dashboard.dupr.com path
  const bareMatch = raw.match(/(?:www\.)?dashboard\.dupr\.com\/[^\s<>"']+/i);
  // Or a numeric DUPR player id alone
  const idOnly = raw.match(/^\s*(\d{6,})\s*$/);

  let candidate = '';
  if (httpMatch) {
    candidate = httpMatch[0];
  } else if (bareMatch) {
    candidate = `https://${bareMatch[0].replace(/^www\./i, '')}`;
  } else if (idOnly) {
    candidate = `https://dashboard.dupr.com/dashboard/player/${idOnly[1]}`;
  } else if (/^https?:\/\//i.test(raw)) {
    candidate = raw;
  } else if (/dashboard\.dupr\.com/i.test(raw) || raw.includes('/')) {
    candidate = `https://${raw.replace(/^\/+/, '')}`;
  } else {
    return '';
  }

  // Strip trailing punctuation from chat paste ("…profile.")
  candidate = candidate.replace(/[.,);\]>'"`]+$/g, '');

  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    // Keep player profile paths usable even without /profile
    const playerMatch = parsed.pathname.match(/\/dashboard\/player\/(\d+)(?:\/profile)?\/?/i);
    if (playerMatch) {
      return `https://dashboard.dupr.com/dashboard/player/${playerMatch[1]}`;
    }
    return parsed.toString().replace(/\/$/, '');
  } catch {
    return '';
  }
};

/** True when paste resolves to an openable DUPR (or generic http) profile URL. */
export const isDuprProfileUrl = (value) => {
  const url = normalizeDuprProfileUrl(value);
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/** Tournament kit / registration t-shirt sizes. */
export const TSHIRT_SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

/** Players package inclusions for every category (singles, doubles, mixed, and open). */
export const PLAYERS_PACKAGE_INCLUSIONS = ['Tshirt', 'Stickers'];
export const playersPackageInclusionsLabel = (items) => {
  const list = (Array.isArray(items) && items.length) ? items : PLAYERS_PACKAGE_INCLUSIONS;
  return `Players package: ${list.join(' · ')}`;
};

export const registrationPlayerFieldOrder = ({
  doubles = false,
  nameLocked = false,
  genderLocked = false,
  partnerGenderLocked = false,
  kids = false,
  captainNeedsParentId = false,
  partnerNeedsParentId = false,
  captainNeedsWaiver = false,
  partnerNeedsWaiver = false,
  captainNeedsJuniorWaiver = false,
  partnerNeedsJuniorWaiver = false,
  skipDupr = false,
} = {}) => {
  const keys = [];
  if (!nameLocked) keys.push('full_name');
  if (!genderLocked) keys.push('gender');
  keys.push('age', 'email', 'contact_number');
  if (kids || captainNeedsParentId) {
    keys.push('junior_parent_id_type', 'junior_parent_id_other', 'junior_parent_id_file_id');
  }
  if (captainNeedsJuniorWaiver) keys.push('junior_waiver_file_id');
  if (captainNeedsWaiver) keys.push('senior_waiver_file_id');
  if (doubles) {
    keys.push('partner_full_name');
    if (!partnerGenderLocked) keys.push('partner_gender');
    keys.push('partner_age', 'partner_contact_number');
    if (kids || partnerNeedsParentId) {
      keys.push(
        'partner_junior_parent_id_type',
        'partner_junior_parent_id_other',
        'partner_junior_parent_id_file_id',
      );
    }
    if (partnerNeedsJuniorWaiver) keys.push('partner_junior_waiver_file_id');
    if (partnerNeedsWaiver) keys.push('partner_senior_waiver_file_id');
  }
  keys.push(
    'playing_experience',
    'previous_winner',
    'previous_winner_details',
    'previous_winner_proof',
  );
  if (!skipDupr) {
    keys.push('dupr_rating', 'dupr_profile_url');
  }
  keys.push('tshirt_size');
  if (doubles) keys.push('partner_tshirt_size');
  return keys;
};

export const isRegistrationPlayerFieldEmpty = (form, key) => {
  const filled = (value) => String(value || '').trim();
  if (key === 'playing_experience') return !filled(form?.playing_experience);
  if (key === 'previous_winner') {
    return form?.previous_winner !== true && form?.previous_winner !== false;
  }
  if (key === 'senior_waiver_file_id' || key === 'partner_senior_waiver_file_id') {
    return !filled(form?.[key]);
  }
  if (key === 'junior_waiver_file_id' || key === 'partner_junior_waiver_file_id') {
    return !filled(form?.[key]);
  }
  if (key === 'previous_winner_details') {
    return form?.previous_winner === true && !filled(form?.previous_winner_details);
  }
  if (key === 'previous_winner_proof') {
    return form?.previous_winner === true
      && !filled(form?.previous_winner_proof_url)
      && !filled(form?.previous_winner_proof_file_id);
  }
  if (key === 'junior_parent_id_type' || key === 'partner_junior_parent_id_type') {
    return !JUNIOR_PARENT_ID_TYPE_IDS.includes(String(form?.[key] || '').trim().toLowerCase());
  }
  if (key === 'junior_parent_id_other') {
    return String(form?.junior_parent_id_type || '').trim().toLowerCase() === 'other'
      && !filled(form?.junior_parent_id_other);
  }
  if (key === 'partner_junior_parent_id_other') {
    return String(form?.partner_junior_parent_id_type || '').trim().toLowerCase() === 'other'
      && !filled(form?.partner_junior_parent_id_other);
  }
  if (key === 'junior_parent_id_file_id' || key === 'partner_junior_parent_id_file_id') {
    return !filled(form?.[key]);
  }
  if (key === 'dupr_rating') return parseDuprRating(form?.dupr_rating) == null;
  if (key === 'dupr_profile_url') return !isDuprProfileUrl(form?.dupr_profile_url);
  if (key === 'age' || key === 'partner_age') {
    return form?.[key] === '' || form?.[key] == null;
  }
  return !filled(form?.[key]);
};

/** First incomplete required Player Information field id, or null. */
export const firstEmptyRegistrationPlayerFieldId = (form, opts = {}) => {
  const order = registrationPlayerFieldOrder(opts);
  const start = opts.afterKey ? order.indexOf(opts.afterKey) + 1 : 0;
  for (let i = Math.max(0, start); i < order.length; i += 1) {
    const key = order[i];
    if (isRegistrationPlayerFieldEmpty(form, key)) {
      return REGISTRATION_PLAYER_FIELD_IDS[key] || null;
    }
  }
  return null;
};

export const defaultTournamentForm = () => ({
  title: '',
  category: 'mens_singles',
  skill_level: 'Novice',
  skill_tier: SKILL_TIER_NONE,
  age_division: AGE_DIVISION_OPEN,
  age_group: AGE_GROUP_10_70,
  tournament_date: '',
  start_time: '08:30',
  end_time: '17:00',
  entry_fee: 1200,
  /** Doubles/mixed only: when true, charge entry_fee × 2 (per player). Default false = amount as entered. */
  entry_fee_per_player: false,
  max_slots: 16,
  draw_format: DRAW_FORMAT_ROUND_ROBIN,
  registration_deadline: '',
  description: '',
  venue_name: DEFAULT_TOURNAMENT_VENUE,
  group_chat_url: '',
});

/**
 * First PH calendar week after create: ₱1100 singles / ₱2100 doubles (all categories),
 * capped at the first 6 singles players or 5 doubles/mixed teams. Then regular ₱1200 / ₱2400.
 */
export const PH_TIME_ZONE = 'Asia/Manila';
export const EARLY_BIRD_DAYS = 7;
export const EARLY_BIRD_SINGLES_FEE = 1100;
export const EARLY_BIRD_DOUBLES_FEE = 2100;
export const REGULAR_SINGLES_FEE = 1200;
export const REGULAR_DOUBLES_FEE = 2400;
export const EARLY_BIRD_DOUBLES_SLOT_CAP = 5;
export const EARLY_BIRD_SINGLES_SLOT_CAP = 6;

export const earlyBirdSlotCap = (tournament) => (
  isDoublesCategory(tournament?.category)
    ? EARLY_BIRD_DOUBLES_SLOT_CAP
    : EARLY_BIRD_SINGLES_SLOT_CAP
);

export const phCalendarDateIso = (value = new Date()) => {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: PH_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const y = get('year');
  const m = get('month');
  const day = get('day');
  if (!y || !m || !day) return '';
  return `${y}-${m}-${day}`;
};

export const earlyBirdStartDateIso = (tournament) => {
  const created = tournament?.created_at || tournament?.createdAt;
  if (!created) return '';
  return phCalendarDateIso(created);
};

export const earlyBirdEndDateIso = (tournament) => {
  if (tournament?.early_bird_ends_on) return String(tournament.early_bird_ends_on).slice(0, 10);
  const start = earlyBirdStartDateIso(tournament);
  if (!start) return '';
  return addCalendarDaysIso(start, EARLY_BIRD_DAYS - 1);
};

export const isEarlyBirdWeek = (tournament, now = new Date()) => {
  const start = earlyBirdStartDateIso(tournament);
  const end = earlyBirdEndDateIso(tournament);
  if (!start || !end) return false;
  const today = phCalendarDateIso(now);
  return !!today && today >= start && today <= end;
};

export const isEarlyBirdActive = (tournament, now = new Date()) => {
  if (typeof tournament?.early_bird_active === 'boolean') {
    return tournament.early_bird_active;
  }
  if (!isEarlyBirdWeek(tournament, now)) return false;
  if (Number.isFinite(Number(tournament?.early_bird_slots_left))) {
    return Number(tournament.early_bird_slots_left) > 0;
  }
  return true;
};

const formatPesoAmount = (amount) =>
  `₱${Number(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Regular price after early bird.
 * Singles / Open Singles: ₱1200 (never ×2).
 * Men's / Women's / Mixed doubles: ₱2400 pair total (don't turn 2400 into 4800).
 */
export const resolveRegularTournamentEntryFee = (tournament) => {
  const n = Number(tournament?.entry_fee);
  const doubles = isDoublesCategory(tournament?.category);
  const perPlayer = !!tournament?.entry_fee_per_player;
  const fromFields = () => {
    if (!Number.isFinite(n) || n <= 0) return 0;
    if (doubles) {
      if (perPlayer) return REGULAR_DOUBLES_FEE;
      return Math.round(n * 100) / 100;
    }
    if (n >= REGULAR_DOUBLES_FEE) return REGULAR_SINGLES_FEE;
    return Math.round(n * 100) / 100;
  };
  const computed = fromFields();
  const serverRegular = Number(tournament?.regular_entry_fee);
  if (Number.isFinite(serverRegular) && serverRegular > 0) {
    const rounded = Math.round(serverRegular * 100) / 100;
    if (!doubles && rounded > REGULAR_SINGLES_FEE) {
      return computed || REGULAR_SINGLES_FEE;
    }
    if (doubles && rounded > REGULAR_DOUBLES_FEE) {
      return computed || REGULAR_DOUBLES_FEE;
    }
    return rounded;
  }
  return computed;
};

/**
 * Amount charged for one registration right now (early bird during week 1, while slots remain).
 */
export const resolveTournamentEntryFee = (tournament) => {
  const regular = resolveRegularTournamentEntryFee(tournament);
  if (Number.isFinite(Number(tournament?.effective_entry_fee))) {
    const eff = Math.round(Number(tournament.effective_entry_fee) * 100) / 100;
    if (eff >= 0) return eff;
  }
  if (regular <= 0) return 0;
  if (!isEarlyBirdActive(tournament)) return regular;
  const early = isDoublesCategory(tournament?.category)
    ? EARLY_BIRD_DOUBLES_FEE
    : EARLY_BIRD_SINGLES_FEE;
  return Math.round(Math.min(early, regular) * 100) / 100;
};

export const formatTournamentEntryFee = (tournament, { withSuffix = true } = {}) => {
  const amount = resolveTournamentEntryFee(tournament);
  const formatted = formatPesoAmount(amount);
  if (!withSuffix) return formatted;
  const doubles = isDoublesCategory(tournament?.category);
  if (isEarlyBirdActive(tournament) && amount > 0) {
    return doubles ? `${formatted} Early bird (per pair)` : `${formatted} Early bird`;
  }
  if (!doubles) return formatted;
  return tournament?.entry_fee_per_player
    ? `${formatted} (per player × 2)`
    : `${formatted} (per pair)`;
};

export const earlyBirdFeeHint = (tournament) => {
  const regular = resolveRegularTournamentEntryFee(tournament);
  const live = resolveTournamentEntryFee(tournament);
  if (regular <= 0 || live <= 0) return '';
  if (!isEarlyBirdActive(tournament) || live >= regular) return '';
  const end = earlyBirdEndDateIso(tournament);
  const endLabel = end ? formatTournamentDate(end) : '';
  const cap = Number(tournament?.early_bird_slot_cap);
  const left = Number(tournament?.early_bird_slots_left);
  const pairWord = isDoublesCategory(tournament?.category) ? 'pairs' : 'players';
  const slotBit = Number.isFinite(cap) && Number.isFinite(left) && cap > 0
    ? `${left} of ${cap} ${pairWord} left this week`
    : '';
  if (slotBit && endLabel) {
    return `Early bird: ${slotBit}. Regular ${formatPesoAmount(regular)} after that or from ${endLabel} onward.`;
  }
  if (slotBit) {
    return `Early bird: ${slotBit}. Regular ${formatPesoAmount(regular)} after the promo slots or week 1.`;
  }
  return endLabel
    ? `Early bird this week (Philippines calendar). Regular ${formatPesoAmount(regular)} from ${endLabel} onward.`
    : `Early bird this week (Philippines calendar). Regular ${formatPesoAmount(regular)} after week 1.`;
};

/** Landing banner panel colors + fallback hero photos per category. */
export const CATEGORY_BANNER_THEMES = {
  mens_singles: {
    panel: '#5BA4D9',
    accent: '#194e7f',
    fallbackImage: '/newassets/b1.jpg',
  },
  mens_doubles: {
    panel: '#4EC4D9',
    accent: '#0e7490',
    fallbackImage: '/newassets/b2.jpg',
  },
  womens_singles: {
    panel: '#E84A8A',
    accent: '#be185d',
    fallbackImage: '/newassets/b4.jpg',
  },
  womens_doubles: {
    panel: '#B191FF',
    accent: '#7c3aed',
    fallbackImage: '/newassets/b5.jpg',
  },
  mixed_doubles: {
    panel: '#2BB673',
    accent: '#15803d',
    fallbackImage: '/newassets/b2.jpg',
  },
  open_mens_singles: {
    panel: '#1e4b85',
    accent: '#0f2747',
    fallbackImage: '/newassets/b5.jpg',
  },
  open_womens_singles: {
    panel: '#7c3aed',
    accent: '#4c1d95',
    fallbackImage: '/newassets/b5.jpg',
  },
  open_mens_doubles: {
    panel: '#0e7490',
    accent: '#164e63',
    fallbackImage: '/newassets/b2.jpg',
  },
  open_womens_doubles: {
    panel: '#6d28d9',
    accent: '#4c1d95',
    fallbackImage: '/newassets/b5.jpg',
  },
  open_mixed_doubles: {
    panel: '#15803d',
    accent: '#14532d',
    fallbackImage: '/newassets/b2.jpg',
  },
};

/** Resolve banner theme; maps legacy mixed IDs to unified Mixed Doubles. */
export const getCategoryBannerTheme = (categoryId) => {
  const normalized = normalizeCategoryId(categoryId);
  if (CATEGORY_BANNER_THEMES[normalized]) return CATEGORY_BANNER_THEMES[normalized];
  if (isMixedCategory(normalized)) return CATEGORY_BANNER_THEMES.mixed_doubles;
  if (isDoublesCategory(normalized)) return CATEGORY_BANNER_THEMES.mens_doubles;
  return CATEGORY_BANNER_THEMES.mens_singles;
};

export const TOURNAMENT_LOGO = '/newassets/logo%201.png';

/** Format YYYY-MM-DD → DD/MM/YYYY (e.g. 2026-07-12 → 12/07/2026). */
export const formatTournamentDate = (isoDate) => {
  if (!isoDate) return '—';
  const [y, m, d] = String(isoDate).split('-');
  if (!y || !m || !d) return isoDate;
  return `${d}/${m}/${y}`;
};

/** Format YYYY-MM-DD → long US style (e.g. 2026-08-03 → August 3, 2026). */
export const formatTournamentDateLong = (isoDate) => {
  if (!isoDate) return '—';
  try {
    const raw = String(isoDate).trim();
    const dayPart = raw.includes('T') ? raw.slice(0, 10) : raw.slice(0, 10);
    const d = new Date(`${dayPart}T12:00:00`);
    if (Number.isNaN(d.getTime())) return formatTournamentDate(isoDate);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return formatTournamentDate(isoDate);
  }
};
